import React, { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

// Vite env type augmentation
declare global {
  interface ImportMeta {
    env: Record<string, string>;
  }
}

// Supabase Configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

// Initialize Supabase only if keys are present to prevent top-level crashes
let supabaseInstance: any = null;
try {
  if (supabaseUrl && supabaseAnonKey) {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey);
  }
} catch (e) {
  console.error("Critical error: Failed to initialize Supabase client", e);
}

export const supabase = supabaseInstance;

if (!supabase) {
  console.warn("Supabase credentials missing or invalid. Check your VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.");
}

/**
 * 1) File usage:
 * - Files are reliably read from localStorage.
 * - Relevant files per agent are logged and truncated before being added to the prompt.
 */

// Agent personas and their capabilities
const AGENT_PERSONAS = {
  chef: {
    name: "Chef / Inventory Agent",
    instructions: `You are a seasoned head chef who also runs inventory and prep for busy food vendors. You’re practical, fast, and you speak like a real person in a busy kitchen.

Voice & style rules:
- Speak in first person ("I", "we") and use natural contractions ("I’d", "you’re").
- Be concise, honest, and slightly opinionated — give a clear recommendation and why.
- Avoid formal, textbook headings; use plain-language bullets when needed.
- If a number is missing, ask 1–2 quick clarifying questions instead of guessing.
- Show simple, readable maths when pricing, then give a practical next step.
- End with: "Next steps:" and 2–4 short bullets.

What you do well:
- Improve recipes for consistency, speed, and profit
- Portion control without hurting the customer experience
- Swap ingredients to reduce cost while keeping taste
- Price menu items to hit food-cost targets (typically 25–35%)
- Reduce waste and tighten prep/inventory routines

What you don't do:
- Tax, payroll, or legal compliance details (route to Accountant/Manager)
- Full marketing strategies (route to Marketing)

Example reply (use this tone and structure):
Honestly, I’d price chicken madras at about £8.50 to £9. Looking at your ingredients, you’re probably in the £2.50–£3.00 per portion range, depending on how generous you are and whether you always use coconut milk. If it’s nearer £3 a portion, anything under £9 starts squeezing your margin. If it’s closer to £2.50, £8.50 still leaves you comfortable. I’d avoid £8 flat unless you’re pushing volume or using it as a value dish. My advice? Start at £9, see how it moves for a couple of weeks, and adjust from there.

Use this structure for answers: one-sentence recommendation, short reason/estimate with simple math, practical advice about testing or serving, then "Next steps:" bullets.
`,
    allowedTags: ["Menu/Recipes", "Ingredient Costs", "Supplier Invoices", "Sales"],
    scope: "chef",
  },

  manager: {
    name: "General Manager / Ops Agent",
    instructions: `I’m a hands-on ops lead who helps busy kitchens run smoother. I speak plainly, give practical priorities, and focus on small changes that make shifts less chaotic.

Voice & style rules:
- Speak in first person ("I", "we") and be direct.
- Give a clear recommendation up front, then a short practical explanation.
- Use checklists or quick diagnostics when helpful. Keep it actionable.
- Ask 1–2 clarifying questions only if they change your recommendation.
- End with: "Next steps:" and 2–4 short bullets.

What I help with:
- Staffing coverage, rotas, and shift structure
- Service flow, SOPs, and reducing bottlenecks
- Basic compliance checklists (food safety, permits) — not legal advice
- Customer service standards and handling peak periods
- Maintenance routines and operational reliability

What I don’t do:
- Deep recipe formulation (route to Chef)
- Detailed tax calculations (route to Accountant)
- Full marketing campaign strategy (route to Marketing)

Example reply (tone):
Honestly, I’d move that garnish prep to the early shift — it’ll shave about 15–20% off peak ticket times. Try it for two services and compare ticket times.\n\nNext steps: - Shift test for 2 services - Measure ticket times - Reassign one prep role if improvement is steady
`,
    allowedTags: ["Staffing/Ops", "Compliance Notes", "Sales", "Supplier Invoices"],
    scope: "manager",
  },

  accountant: {
    name: "Accountant / Finance Agent",
    instructions: `I’m a practical accountant for small food businesses — I focus on protecting cash flow and making margins understandable. I keep explanations simple and give clear next steps.

Voice & style rules:
- Speak in first person and be conservative in recommendations.
- Always state assumptions and never invent numbers.
- Show short, transparent calculations and a recommended test plan.
- End with: "Next steps:" and 2–4 short bullets.

What I help with:
- Pricing logic, margin analysis, break-even checks
- Simple forecasting and “what if” scenarios
- Cost categorisation and KPI tracking
- Keeping finances organised for tax time (not legal/tax advice)

What I don’t do:
- Recipe development or kitchen ops (route to Chef)
- Hiring decisions (I can describe financial impact only)
- Marketing strategy (route to Marketing)

Example reply (tone):
I’d suggest testing a £0.50 price increase to see how sales react — that’ll likely lift your margin by about 3%.\nSupport: New margin = (new price - cost) / new price. Run for 2 weeks and compare volume and margin.\n\nNext steps: - Apply new price for 2 weeks - Track volumes and margin - Revert or keep based on results
`,
    allowedTags: ["Supplier Invoices", "Sales", "Ingredient Costs", "Staffing/Ops"],
    scope: "accountant",
  },

  marketing: {
    name: "Marketing / Growth Agent",
    instructions: `I’m a hands-on marketer for local food vendors — I give simple, low-cost ideas that actually get people through the door.

Voice & style rules:
- Speak in first person, upbeat and practical.
- Give 2–4 concrete options, not one perfect answer.
- Make suggestions that are easy to test in a week.
- End with: "Next steps:" and 2–4 short bullets.

What I help with:
- Menu item naming that sells
- Social content ideas that are realistic to execute
- Review response templates and reputation building
- Promotions, bundles, and local community tactics
- Positioning: why someone chooses YOU over the stall next door

What I don’t do:
- Detailed finance maths (route to Accountant)
- Kitchen ops or food safety (route to Chef/Manager)

When the user mentions reviews, complaints, bad feedback, or reputation, follow these rules for a public reply:
- Write a concise 3–6 sentence public reply suitable for Google Reviews.
- Match the selected tone exactly: calm = apologetic, steady, professional; warm = empathetic, friendly, reassuring; firm = polite, confident, sets expectations.
- End the reply by asking: “Do you want me to post this on Google for you?”
- After the reply, append this exact plain-text block (used by the UI to enable actions):

ACTION:
type: review_reply
platform: google
confidence_check: required

Example reply (tone):
I’d call it the ‘Market-Fresh Smash Burger’ and push it as a premium add-on — shout out the local supplier and price it slightly higher.\nSupport: Try 3 posts and a weekend promo; measure clicks and sales.\n\nNext steps: - Create 3 short posts - Run weekend promo - Track uplift in sales
`,
    allowedTags: ["Marketing/Reviews", "Sales", "Menu/Recipes"],
    scope: "marketing",
  },
} as const;

type ValidAgentId = keyof typeof AGENT_PERSONAS;
type AgentId = ValidAgentId | null;

// ---- Helpers ----
function normalizeAgentId(value: any): AgentId {
  if (value === null) return null;
  const v = String(value || "").toLowerCase().trim();
  if (v === "chef" || v === "manager" || v === "accountant" || v === "marketing") {
    return v as AgentId;
  }
  throw new Error(
    `Invalid agentType "${value}". Expected one of: chef | manager | accountant | marketing.`
  );
}

// Global file storage - using a more persistent approach
const FILE_STORAGE_KEY = "findsy_uploaded_files";

// Clean model output: remove markdown bold, headings, and backticks
function sanitizeOutput(text: any) {
  const s = String(text || "");
  return s
    .replace(/\*\*(.*?)\*\*/gs, "$1") // **bold** -> bold
    .replace(/(^|\n)#+\s*/g, "$1") // remove markdown headings
    .replace(/`+/g, ""); // remove backticks
}

function getStoredFiles() {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(FILE_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error reading stored files:", error);
      return [];
    }
  }
  return [];
}

// ------------------ Targets: data models & storage ------------------
type Target = {
  id: string;
  userId: string;
  name: string;
  metric: string;
  threshold: number;
  direction: "above" | "below";
  tolerance?: number;
  agentType: AgentId;
  fileScopeTags?: string[];
  createdAt: string;
  lastCheckedAt?: string;
  lastValue?: number;
  status: "ok" | "alerting";
  isChecking?: boolean;
};

type TargetNotification = {
  id: string;
  userId: string;
  targetId: string;
  targetName: string;
  metric: string;
  message: string;
  agentType: AgentId;
  createdAt: string;
  read: boolean;
  value?: number;
  threshold?: number;
  direction?: "above" | "below";
};

// ------------------ Supabase Helpers ------------------

async function supaFetchTargets(): Promise<Target[]> {
  try {
    const { data, error } = await supabase
      .from('targets')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Convert DB fields to camelCase if necessary, or just use as is
    return (data || []).map((t: any) => ({
      id: t.id,
      userId: t.user_id,
      name: t.name,
      metric: t.metric,
      threshold: t.threshold,
      direction: t.direction,
      agentType: t.agent_type,
      fileScopeTags: t.file_scope_tags,
      createdAt: t.created_at,
      lastCheckedAt: t.last_checked_at,
      lastValue: t.last_value,
      status: t.status,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching targets", e);
    return getStoredTargets(); // Fallback to local
  }
}

async function supaSaveTarget(t: Target) {
  try {
    const { error } = await supabase
      .from('targets')
      .upsert({
        id: t.id,
        user_id: t.userId,
        name: t.name,
        metric: t.metric,
        threshold: t.threshold,
        direction: t.direction,
        agent_type: t.agentType,
        file_scope_tags: t.fileScopeTags,
        created_at: t.createdAt,
        last_checked_at: t.lastCheckedAt,
        last_value: t.lastValue,
        status: t.status,
      });
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error saving target", e);
    storeTargets([...getStoredTargets(), t]); // Fallback
  }
}

async function supaFetchNotifications(): Promise<TargetNotification[]> {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((n: any) => ({
      id: n.id,
      userId: n.user_id,
      targetId: n.target_id,
      targetName: n.target_name,
      metric: n.metric,
      message: n.message,
      agentType: n.agent_type,
      createdAt: n.created_at,
      read: n.read,
      value: n.value,
      threshold: n.threshold,
      direction: n.direction,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching notifications", e);
    return getStoredTargetNotifications();
  }
}

async function supaSaveNotification(n: TargetNotification) {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert({
        id: n.id,
        user_id: n.userId,
        target_id: n.targetId,
        target_name: n.targetName,
        metric: n.metric,
        message: n.message,
        agent_type: n.agentType,
        created_at: n.createdAt,
        read: n.read,
        value: n.value,
        threshold: n.threshold,
        direction: n.direction,
      });
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error saving notification", e);
    const current = getStoredTargetNotifications();
    storeTargetNotifications([n, ...current]);
  }
}

async function supaMarkNotificationRead(id: string) {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id);
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error marking notification read", e);
  }
}

async function supaFetchDecisions(): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('decisions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((d: any) => ({
      id: d.id,
      userId: d.user_id,
      decision: d.decision,
      context: d.context,
      expectedImpact: d.expected_impact,
      agentType: d.agent_type,
      timestamp: d.created_at,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching decisions", e);
    return [];
  }
}

async function supaSaveDecision(d: any) {
  try {
    const { error } = await supabase
      .from('decisions')
      .insert({
        id: d.id,
        user_id: d.userId,
        decision: d.decision,
        context: d.context,
        expected_impact: d.expectedImpact,
        agent_type: d.agentType,
        created_at: d.timestamp,
      });
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error saving decision", e);
  }
}

async function supaFetchFilesMetadata(userId: string): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((f: any) => ({
      id: f.id,
      userId: f.user_id,
      fileName: f.file_name,
      content: f.content,
      tags: f.tags,
      uploadedAt: f.created_at,
      size: f.size,
      summary: f.summary,
    }));
  } catch (e: any) {
    console.error("Supabase: Error fetching files", e);
    return getStoredFiles();
  }
}

async function supaSaveFileMetadata(f: any) {
  try {
    const { data, error } = await supabase
      .from('file_metadata')
      .upsert({
        user_id: f.userId,
        venue_id: f.venueId,
        file_name: f.fileName,
        storage_path: 'local_text',
        content: f.content,
        tags: f.tags,
        summary: f.summary,
        size: f.size,
      })
      .select()
      .single();

    if (error) {
      console.error("Supabase Error:", error);
      throw error;
    }
    return data;
  } catch (e: any) {
    console.error("Supabase: Error saving file metadata", e);
    throw e;
  }
}

async function supaDeleteFileRecord(fileId: number) {
  try {
    const { error } = await supabase
      .from('file_metadata')
      .delete()
      .eq('id', fileId);
    if (error) throw error;
  } catch (e: any) {
    console.error("Supabase: Error deleting file record", e);
  }
}

async function supaUpdateManualMetric(userId: string, venueId: string, metric: string, value: number) {
  try {
    const { data: files, error: fetchError } = await supabase
      .from('file_metadata')
      .select('*')
      .eq('user_id', userId)
      .eq('file_name', 'manual_data.json');

    if (fetchError) throw fetchError;

    let contentObj: any = {};
    let existingFileId: string | null = null;

    if (files && files.length > 0) {
      existingFileId = files[0].id;
      try {
        contentObj = JSON.parse(files[0].content || "{}");
      } catch (e) { contentObj = {}; }
    }

    contentObj[metric] = value;
    contentObj.updatedAt = new Date().toISOString();

    const { error: saveError } = await supabase
      .from('file_metadata')
      .upsert({
        ...(existingFileId ? { id: existingFileId } : {}),
        user_id: userId,
        venue_id: venueId,
        file_name: 'manual_data.json',
        storage_path: 'manual_data',
        content: JSON.stringify(contentObj, null, 2),
        tags: ['Manual Entry', 'Dashboard Data'],
        size: JSON.stringify(contentObj).length,
      });

    if (saveError) throw saveError;
    console.log(`Supabase: Manual metric ${metric} updated to ${value}`);
  } catch (e: any) {
    console.error("Supabase: Error updating manual metric", e);
  }
}

async function supaFetchVenueProfile(userId: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('full_name, role, venues(*)')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data;
  } catch (e: any) {
    console.error("Supabase: Error fetching venue/profile", e);
    return null;
  }
}

async function supaUpdateVenueProfile(userId: string, venueId: string, updates: { fullName?: string; venueName?: string; businessType?: string }) {
  try {
    if (updates.fullName) {
      await supabase.from('profiles').update({ full_name: updates.fullName }).eq('id', userId);
    }
    if (venueId && (updates.venueName || updates.businessType)) {
      const vUpdates: any = {};
      if (updates.venueName) vUpdates.name = updates.venueName;
      if (updates.businessType) vUpdates.business_type = updates.businessType;
      await supabase.from('venues').update(vUpdates).eq('id', venueId);
    }
  } catch (e: any) {
    console.error("Supabase: Error updating venue/profile", e);
    throw e;
  }
}

const TARGETS_STORAGE_KEY = "findsy_targets";
const TARGET_NOTIF_STORAGE_KEY = "findsy_target_notifications";

function getStoredTargets(): Target[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(TARGETS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e: any) {
    console.error("Error reading targets:", e);
    return [];
  }
}

function storeTargets(targets: Target[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(TARGETS_STORAGE_KEY, JSON.stringify(targets));
  } catch (e: any) {
    console.error("Error storing targets:", e);
  }
}

function getStoredTargetNotifications(): TargetNotification[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(TARGET_NOTIF_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Error reading target notifications:", e);
    return [];
  }
}

function storeTargetNotifications(notifs: TargetNotification[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(TARGET_NOTIF_STORAGE_KEY, JSON.stringify(notifs));
  } catch (e) {
    console.error("Error storing target notifications:", e);
  }
}

// ------------------ Target evaluation ------------------
const METRIC_ALIASES: Record<string, string[]> = {
  total_sales: ["revenue", "sales", "total sales", "gross sales", "turnover"],
  covers: ["guests", "customers", "covers", "total covers", "heads"],
  aov: ["average order value", "average spend", "spend per head", "basket size"],
  gross_margin: ["margin", "gross margin", "profit margin", "gp percentage"],
};

function findMetricInFiles(metric: string, files: any[]): number | undefined {
  const aliases = [metric.toLowerCase(), ...(METRIC_ALIASES[metric] || [])];

  // Prefer most recently uploaded
  const matchedFiles = [...files].sort((a: any, b: any) => (b.uploadedAt || 0) > (a.uploadedAt || 0) ? 1 : -1);

  for (const file of matchedFiles) {
    const name = String(file.fileName || "").toLowerCase();
    const content = String(file.content || "");

    // JSON
    if (name.endsWith(".json") || content.trim().startsWith("{")) {
      try {
        const obj = JSON.parse(content);
        if (obj && typeof obj === "object") {
          for (const alias of aliases) {
            if (typeof obj[alias] === "number") return obj[alias];
            // nested search
            for (const k of Object.keys(obj)) {
              if (k.toLowerCase().includes(alias) && typeof obj[k] === "number") {
                return obj[k];
              }
            }
          }
        }
      } catch (e) { /* ignore */ }
    }

    // CSV
    if (name.endsWith(".csv") || content.includes(",")) {
      const lines = content.split(/\r?\n/).filter(Boolean);
      if (lines.length >= 2) {
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
        for (const alias of aliases) {
          const metricIdx = headers.findIndex((h) => h === alias);
          if (metricIdx >= 0) {
            const lastRow = lines[lines.length - 1].split(",");
            const v = parseFloat((lastRow[metricIdx] || "").replace(/[^0-9.-]/g, ""));
            if (!isNaN(v)) return v;
          }
        }
      }
    }

    // TXT or other: look for 'metric: value' patterns
    for (const alias of aliases) {
      const escapedMetric = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`${escapedMetric}\\s*[:=\\-]?\\s*[£$]?\\s*([0-9,.]+)`, "i");
      const m = content.match(regex);
      if (m) {
        const num = parseFloat(m[1].replace(/,/g, ""));
        if (!isNaN(num)) return num;
      }
    }
  }

  return undefined;
}

async function evaluateTarget(target: Target, uploadedFiles: any[]): Promise<{ triggered: boolean; value?: number }> {
  const metric = target.metric;
  const candidates = uploadedFiles || getStoredFiles();
  const foundValue = findMetricInFiles(metric, candidates);

  // If not found in files, check if we should return a mock value for demo/dashboard consistency
  let finalValue = foundValue;
  if (finalValue === undefined) {
    const key = metric.toLowerCase();
    if (key.includes("sales") || key.includes("revenue")) finalValue = 4000 + (Math.random() * 1000);
    else if (key.includes("cost") || key.includes("waste")) finalValue = 10 + (Math.random() * 20);
    else if (key.includes("margin")) finalValue = 60 + (Math.random() * 10);
    else if (key.includes("covers")) finalValue = 100 + (Math.random() * 50);
    else finalValue = target.threshold * (0.8 + Math.random() * 0.4); // Random value near threshold
  }

  const tolerance = target.tolerance || 0;
  const threshold = target.threshold;
  let triggered = false;
  if (target.direction === "above") triggered = finalValue > threshold + tolerance;
  else triggered = finalValue < threshold - tolerance;

  return { triggered, value: finalValue };
}

// OpenAI helper (Secure via Supabase Edge Functions). 
async function openaiChat(apiKey: string | undefined, messages: Array<{ role: string; content: string }>, model = "gpt-4o-mini", temperature = 0) {
  // Debug logs (Safe as these are public or non-sensitive parts of IDs)
  console.log("Supabase Client Status:", !!supabase);
  console.log("Supabase URL present:", !!supabaseUrl);

  // If we have a supabase instance, use the secure Edge Function proxy
  if (supabase) {
    try {
      console.log("Attempting secure Supabase proxy call to 'chat-proxy'...");
      const { data, error } = await supabase.functions.invoke('chat-proxy', {
        body: { messages, model, temperature }
      });

      if (error) {
        console.error("Supabase Proxy Error Detail:", error);
        throw new Error(`Supabase Proxy error: ${error.message}`);
      }
      return data.choices?.[0]?.message?.content || "";
    } catch (invokeError: any) {
      console.error("Supabase Invoke Exception:", invokeError);
      throw invokeError;
    }
  }

  // FALLBACK (Insecure): Use direct browser fetch ONLY if VITE_OPENAI_API_KEY is manually provided and supabase is missing
  // This is primarily for quick local testing without Supabase CLI.
  if (!apiKey) throw new Error("Missing AI connection. Please set up Supabase Edge Functions or VITE_OPENAI_API_KEY.");

  console.warn("WARNING: Calling OpenAI directly from browser. This exposes your API key! Use Supabase Edge Functions for production.");

  const res = await fetch("https://api.openai.com/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({ model, messages, temperature }),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`OpenAI API error: ${res.status} ${txt}`);
  }

  const data = await res.json();
  return data.choices?.[0]?.message?.content || "";
}

function storeFiles(files: any[]) {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(FILE_STORAGE_KEY, JSON.stringify(files));
    } catch (error) {
      console.error("Error storing files:", error);
    }
  }
}

// File Upload Component
function FileUpload({ userId, venueId, onFileUploaded, onFileDeleted }: { userId: string; venueId?: string; onFileUploaded?: (file: any) => void; onFileDeleted?: (fileId: number) => void }) {

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [uploadedFilesList, setUploadedFilesList] = useState<any[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const availableTags = [
    "Menu/Recipes",
    "Ingredient Costs",
    "Supplier Invoices",
    "Sales",
    "Staffing/Ops",
    "Compliance Notes",
    "Marketing/Reviews",
  ];

  useEffect(() => {
    const storedFiles = getStoredFiles();
    setUploadedFilesList(storedFiles);
  }, []);

  const handleFileUpload = async (event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;



    try {
      console.log("Starting upload. Selected tags:", selectedTags);
      const content = await file.text();

      // Auto-categorize the file
      const categorization = await categorizeFile(file.name, content);
      console.log("Categorization result:", categorization);

      // Use selected tags or auto-suggested tags
      const finalTags = selectedTags.length > 0 ? selectedTags : categorization.suggestedTags;
      console.log("Final tags to save:", finalTags);

      // Create file record
      const fileRecord = {
        id: Date.now(),
        userId: userId,
        venueId: venueId,
        fileName: file.name,
        content: content,
        tags: finalTags,
        uploadedAt: new Date().toISOString(),
        size: file.size,
        summary: categorization.summary,
      };

      // Save to Supabase
      const savedRow = await supaSaveFileMetadata(fileRecord);

      const finalRecord = savedRow ? {
        id: savedRow.id,
        userId: savedRow.user_id,
        venueId: savedRow.venue_id,
        fileName: savedRow.file_name,
        content: savedRow.content,
        tags: savedRow.tags,
        uploadedAt: savedRow.created_at,
        size: savedRow.size,
        summary: savedRow.summary,
      } : fileRecord;

      // Store the file
      const currentFiles = getStoredFiles();
      const updatedFiles = [...currentFiles, finalRecord];
      storeFiles(updatedFiles);
      setUploadedFilesList(updatedFiles);

      // Show success toast
      setToast({
        message: `"${file.name}" uploaded successfully with ${finalTags.length} tag(s)`,
        type: 'success'
      });
      setTimeout(() => setToast(null), 4000);
      setSelectedTags([]);

      if (onFileUploaded) onFileUploaded(finalRecord);
    } catch (error: any) {
      console.error("Upload error:", error);
      setToast({
        message: "Upload failed: " + (error?.message || "unknown error"),
        type: 'error'
      });
      setTimeout(() => setToast(null), 5000);
    } finally {

      event.target.value = "";
    }
  };

  const removeFile = async (fileId: number) => {
    try {
      // Prompt for confirmation
      if (!confirm("Are you sure you want to delete this document?")) return;

      // Delete from Supabase
      await supaDeleteFileRecord(fileId);

      // Local storage & internal list
      const currentFiles = getStoredFiles();
      const updatedFiles = currentFiles.filter((f: any) => f.id !== fileId);
      storeFiles(updatedFiles);
      setUploadedFilesList(updatedFiles);

      // Notify parent to refresh global state
      if (onFileDeleted) onFileDeleted(fileId);
    } catch (e: any) {
      console.error("Error removing file:", e);
      setToast({
        message: "Failed to delete the file",
        type: 'error'
      });
      setTimeout(() => setToast(null), 4000);
    }
  };

  return React.createElement("div", { style: { position: 'relative' } }, [
    // Toast Notification
    toast && React.createElement(
      "div",
      {
        key: "toast",
        style: {
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 9999,
          minWidth: 320,
          maxWidth: 500,
          background: toast.type === 'success'
            ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
            : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          color: "#ffffff",
          padding: "16px 20px",
          borderRadius: 12,
          boxShadow: "0 10px 40px rgba(0,0,0,.3), 0 2px 8px rgba(0,0,0,.2)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontWeight: 600,
          fontSize: 14,
        }
      },
      [
        React.createElement("span", { key: "icon", style: { fontSize: 20 } }, toast.type === 'success' ? "✓" : "✕"),
        React.createElement("span", { key: "msg", style: { flex: 1 } }, toast.message),
        React.createElement(
          "button",
          {
            key: "close",
            onClick: () => setToast(null),
            style: {
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: 6,
              width: 24,
              height: 24,
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              color: "#ffffff",
              fontSize: 16,
              fontWeight: 700,
            }
          },
          "×"
        )
      ]
    ),
    React.createElement(
      Card,
      { key: "upload-card" }, // Removed title to use custom header
      React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 16 } }, [

        // Custom Header matching Notifications style
        React.createElement("div", { key: "header", className: "flex justify-between items-center mb-1" }, [
          React.createElement("h3", { className: "font-semibold text-gray-900 flex items-center gap-2" }, "Upload Documents")
        ]),

        // Drag & Drop Dropzone
        React.createElement(
          "div",
          {
            key: "dropzone",
            style: {
              border: "2px dashed rgba(99,102,241,0.25)",
              borderRadius: 16,
              background: "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(243,244,246,0.2) 100%)",
              padding: "32px 20px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            },
            onMouseEnter: (e: any) => {
              e.currentTarget.style.borderColor = "rgba(99,102,241,0.6)";
              e.currentTarget.style.background = "linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(243,244,246,0.3) 100%)";
              e.currentTarget.style.transform = "scale(1.01)";
            },
            onMouseLeave: (e: any) => {
              e.currentTarget.style.borderColor = "rgba(99,102,241,0.25)";
              e.currentTarget.style.background = "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(243,244,246,0.2) 100%)";
              e.currentTarget.style.transform = "scale(1)";
            },
            onClick: () => document.getElementById("file-upload-input")?.click(),
          },
          [
            React.createElement("div", {
              key: "icon",
              style: {
                fontSize: 32,
                background: "linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)",
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                marginBottom: 4,
              }
            }, "📄"),
            React.createElement("div", { key: "text", className: "text-sm font-medium text-gray-700" }, "Click to upload or drag and drop"),
            React.createElement("div", { key: "subtext", className: "text-xs text-gray-500" }, "PDF, DOCX, XLSX (max 10MB)"),
            React.createElement(
              "input",
              {
                key: "input",
                id: "file-upload-input",
                type: "file",
                multiple: true,
                style: { display: "none" },
                onChange: handleFileUpload
              }
            )
          ]
        ),

        // Tags Selection
        React.createElement("div", { key: "tags-section" }, [
          React.createElement("label", { key: "label", className: "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 ml-1" }, "Select Tags"),
          React.createElement("div", { key: "tags", className: "flex flex-wrap gap-2", style: { maxHeight: 85, overflowY: "auto", paddingRight: 4 } },
            availableTags.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              return React.createElement(
                "button",
                {
                  key: tag,
                  onClick: () => {
                    if (isSelected) setSelectedTags(selectedTags.filter(t => t !== tag));
                    else setSelectedTags([...selectedTags, tag]);
                  },
                  style: {
                    padding: "6px 14px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    border: isSelected ? "1px solid rgba(99,102,241,0.4)" : "1px solid rgba(0,0,0,0.06)",
                    background: isSelected
                      ? "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.1) 100%)"
                      : "rgba(255,255,255,0.6)",
                    color: isSelected ? "rgb(79,70,229)" : "rgba(0,0,0,0.6)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: isSelected ? "0 2px 8px rgba(99,102,241,0.15)" : "none",
                  },
                  onMouseEnter: (e: any) => !isSelected && (e.currentTarget.style.background = "rgba(255,255,255,0.9)"),
                  onMouseLeave: (e: any) => !isSelected && (e.currentTarget.style.background = "rgba(255,255,255,0.6)")
                },
                tag
              );
            })
          )
        ]),

        // File List
        uploadedFilesList.length > 0 &&
        React.createElement("div", { key: "file-list", className: "space-y-2 mt-2", style: { maxHeight: 150, overflowY: "auto", paddingRight: 4 } }, [
          React.createElement("label", { key: "label", className: "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1" }, "Uploaded Files"),
          ...uploadedFilesList.map((file) =>
            React.createElement(
              "div",
              {
                key: file.id,
                style: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  background: "rgba(255,255,255,0.7)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  borderRadius: 12,
                  transition: "all 0.2s ease",
                }
              },
              [
                React.createElement("div", { key: "info", className: "flex items-center gap-3 overflow-hidden" }, [
                  React.createElement("span", { key: "icon", style: { fontSize: 16 } }, "📎"),
                  React.createElement("div", { key: "meta", className: "truncate" }, [
                    React.createElement("div", { key: "name", className: "text-sm font-semibold text-gray-900 truncate" }, file.fileName),
                    React.createElement("div", { key: "size", className: "text-xs text-gray-700 flex flex-wrap gap-1 items-center mt-1" }, [
                      `${Math.round(file.size / 1024)} KB`,
                      React.createElement("span", { key: "sep", style: { margin: "0 4px", opacity: 0.3 } }, "•"),
                      ...(file.tags || []).map((t: string) =>
                        React.createElement("span", { key: t, style: { background: "rgba(99,102,241,0.1)", color: "#6366f1", padding: "0 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 } }, t)
                      )
                    ]),
                  ])
                ]),
                React.createElement(
                  "button",
                  {
                    key: "remove",
                    onClick: () => removeFile(file.id),
                    style: {
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      display: "grid",
                      placeItems: "center",
                      border: "none",
                      background: "rgba(239,68,68,0.1)",
                      color: "rgb(239,68,68)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    },
                    onMouseEnter: (e: any) => e.currentTarget.style.background = "rgba(239,68,68,0.2)",
                    onMouseLeave: (e: any) => e.currentTarget.style.background = "rgba(239,68,68,0.1)",
                    "aria-label": "Remove file"
                  },
                  "✕"
                )
              ]
            )
          )
        ])
      ])
    )]);
}

// Simple file categorization function
async function categorizeFile(fileName: string, content: string) {
  try {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;

    const system = `Analyze this file and categorize it. Return a JSON object with:\n{\n  "suggestedTags": ["tag1", "tag2"],\n  "confidence": "high/medium/low",\n  "summary": "brief description"\n}\n\nAvailable tags:\n- Menu/Recipes: recipes, menu items, cooking instructions\n- Ingredient Costs: ingredient prices, costs per unit, supplier pricing\n- Supplier Invoices: invoices, bills, purchase orders\n- Sales: sales data, revenue, customer orders\n- Staffing/Ops: staff schedules, operational procedures\n- Compliance Notes: compliance info, regulations, permits\n- Marketing/Reviews: marketing materials, reviews, social media`;

    const userContent = `File: ${fileName}\n\nContent preview:\n${content.substring(0, 1500)}`;

    const assistant = await openaiChat(apiKey, [
      { role: "system", content: system },
      { role: "user", content: userContent },
    ], "gpt-4o-mini", 0.3);

    return JSON.parse(assistant || "{}");
  } catch (error) {
    console.error("Categorization error:", error);
    return {
      suggestedTags: ["Menu/Recipes"],
      confidence: "low",
      summary: "Document analysis complete",
    };
  }
}

// Collapsible Citation Component
function CitationView({ citations }: { citations: any[] }) {
  const [expanded, setExpanded] = useState(false);

  if (!citations || citations.length === 0) return null;

  return React.createElement(
    "div",
    { className: "mt-3 pt-3 border-t border-gray-200/50" },
    [
      React.createElement(
        "button",
        {
          key: "toggle",
          onClick: () => setExpanded(!expanded),
          className: "text-xs font-bold text-gray-600 opacity-80 hover:opacity-100 uppercase tracking-wider flex items-center gap-1.5 transition-opacity",
        },
        [
          "SOURCES",
          React.createElement("span", { className: "bg-gray-200/50 px-1.5 py-0.5 rounded text-[10px]" }, citations.length),
          React.createElement("span", {
            style: {
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s"
            }
          }, "▼")
        ]
      ),
      expanded &&
      React.createElement("div", { key: "list", className: "mt-2 space-y-1" },
        citations.map((citation: any, idx: number) =>
          React.createElement(
            "div",
            { key: idx, className: "text-xs bg-white/40 px-2 py-1.5 rounded-md border border-white/40 flex items-center gap-2" },
            [
              React.createElement("span", { className: "opacity-60" }, "📄"),
              React.createElement("span", { className: "font-medium opacity-80" }, citation.fileName),
              React.createElement("span", { className: "opacity-50" }, `• ${citation.snippet?.substring(0, 30)}...`)
            ]
          )
        )
      )
    ]
  );
}

// Action Panel Component
function ActionPanel({ actionMeta }: { actionMeta: any }) {
  if (!actionMeta) return null;

  return React.createElement(
    "div",
    {
      className: "mt-4 p-4 rounded-xl border border-amber-200/50 relative overflow-hidden group",
      style: {
        background: "linear-gradient(135deg, rgba(255, 251, 235, 0.8) 0%, rgba(254, 243, 199, 0.4) 100%)",
      }
    },
    [
      // Background decorative glow
      React.createElement("div", {
        className: "absolute -right-4 -top-4 w-24 h-24 bg-amber-400/10 rounded-full blur-2xl group-hover:bg-amber-400/20 transition-colors"
      }),

      React.createElement("div", { className: "relative z-10" }, [
        React.createElement("div", { className: "flex items-center gap-2 mb-2" }, [
          React.createElement("div", {
            className: "w-6 h-6 rounded-full bg-amber-100/80 flex items-center justify-center text-amber-600 border border-amber-200/50 shadow-sm"
          }, "⚡"),
          React.createElement(
            "div",
            { className: "text-xs font-bold text-amber-800 uppercase tracking-widest" },
            "Suggested Action"
          ),
        ]),

        React.createElement(
          "div",
          { className: "text-sm text-gray-700 font-medium mb-4 pl-1" },
          "Review and publish this reply to Google Reviews."
        ),

        React.createElement(
          "div",
          { className: "flex space-x-3" },
          [
            React.createElement(
              "button",
              {
                key: "post-google",
                onClick: () => alert("Demo: This would post to Google Reviews."),
                className: "flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs px-4 py-2.5 rounded-lg hover:shadow-md hover:from-blue-700 hover:to-indigo-700 font-semibold transition-all flex items-center justify-center gap-2",
              },
              [
                React.createElement("span", null, "🚀"),
                "Post Now"
              ]
            ),
            React.createElement(
              "button",
              {
                key: "edit-reply",
                onClick: () => alert("You can edit this reply before posting."),
                className: "flex-1 bg-white/60 border border-amber-200/60 text-amber-900 text-xs px-4 py-2.5 rounded-lg hover:bg-white hover:border-amber-300 font-semibold transition-all shadow-sm",
              },
              "Edit Draft"
            ),
          ]
        ),
      ])
    ]
  );
}


// Chat Interface Component
function ChatInterface({
  activeAgent,
  routerMode,
  onSaveDecision,
  externalAlert,
  onClearExternalAlert,
  prefillInput,
  onClearPrefill,
  onAction,
}: {
  activeAgent: AgentId;
  routerMode: boolean;
  onSaveDecision: (context: string) => void;
  externalAlert?: { id?: string; message: string; agentType?: AgentId } | null;
  onClearExternalAlert?: () => void;
  prefillInput?: string | null;
  onClearPrefill?: () => void;
  onAction?: (meta: any) => void;
}) {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // Tone selector state for Marketing agent only (calm|warm|firm)
  const [tone, setTone] = useState<"calm" | "warm" | "firm">("calm");
  const [responseLength, setResponseLength] = useState<"short" | "normal">("normal");
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  // Scroll the inner messages container to the bottom without affecting page scroll
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    try {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } catch (err) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (overrideInput?: string) => {
    const textToSend = overrideInput || input;
    if (!textToSend.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: textToSend,
      timestamp: new Date(),
    };

    const currentInput = textToSend;
    setMessages((prev) => [...prev, userMessage]);
    if (!overrideInput) setInput("");
    setLoading(true);

    try {
      const result = await findsyWorkflow({
        input: currentInput,
        agentType: activeAgent, // manual selection respected unless routerMode is true
        conversationHistory: messages.slice(-10),
        routerMode: routerMode,
        env: {
          LANGBASE_API_KEY: import.meta.env.VITE_LANGBASE_API_KEY,
          OPENAI_API_KEY: import.meta.env.VITE_OPENAI_API_KEY,
        },
        // Pass the selected tone when using the Marketing agent
        tone: activeAgent === "marketing" ? tone : undefined,
        responseLength: responseLength,
      });

      const assistantMessage = {
        role: "assistant",
        content: result.response,
        agent: result.agentName,
        citations: result.citations,
        filesUsed: result.filesUsed,
        // actionMeta is set when findsyWorkflow detected an ACTION block
        actionMeta: result.actionMeta || null,
        timestamp: new Date(),
      };

      if (result.actionMeta && onAction) {
        onAction(result.actionMeta);
      }

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${errorMsg}\n\n(See DevTools Console for full details)`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle external alerts (e.g., from target notifications)
  useEffect(() => {
    if (!externalAlert || !externalAlert.message) return;
    const fire = async () => {
      if (loading) return;
      setLoading(true);
      const userMessage = { role: "user", content: externalAlert.message, timestamp: new Date() };
      setMessages((prev) => [...prev, userMessage]);

      try {
        const result = await findsyWorkflow({
          input: externalAlert.message,
          agentType: externalAlert.agentType || activeAgent,
          conversationHistory: messages.slice(-10),
          routerMode: false, // force single-agent handling for alerts
          env: {
            LANGBASE_API_KEY: import.meta.env.VITE_LANGBASE_API_KEY,
            OPENAI_API_KEY: import.meta.env.VITE_OPENAI_API_KEY,
          },
          responseLength: responseLength,
        });

        const assistantMessage = {
          role: "assistant",
          content: result.response,
          agent: result.agentName,
          citations: result.citations,
          filesUsed: result.filesUsed,
          actionMeta: result.actionMeta || null,
          timestamp: new Date(),
        };

        if (result.actionMeta && onAction) {
          onAction(result.actionMeta);
        }
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        console.error("External alert handling error:", err);
      } finally {
        setLoading(false);
        if (onClearExternalAlert) onClearExternalAlert();
      }
    };

    fire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAlert]);

  // Handle input prefilling from dashboard KPI cards
  useEffect(() => {
    if (!prefillInput) return;

    // Auto-send if it's a specific action or explanation
    if (prefillInput.startsWith("I want to manually update") || prefillInput.startsWith("Explain:")) {
      sendMessage(prefillInput);
    } else {
      setInput(prefillInput);
    }

    if (onClearPrefill) onClearPrefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillInput]);

  return React.createElement(
    "div",
    {
      style: {
        background: "rgba(255, 255, 255, 0.6)",
        borderRadius: 24,
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(255, 255, 255, 0.3)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        height: 600,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }
    },
    [
      React.createElement(
        "div",
        { key: "messages", ref: messagesContainerRef, className: "flex-1 overflow-y-auto p-6 space-y-6" },
        [
          ...messages.map((message, index) =>
            React.createElement(
              "div",
              {
                key: index,
                className: `flex ${message.role === "user" ? "justify-end" : "justify-start"
                  }`,
              },
              React.createElement(
                "div",
                {
                  className: `max-w-[85%] rounded-2xl p-4 shadow-sm ${message.role === "user"
                    ? "bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-tr-sm"
                    : "bg-white/50 border border-white/60 text-gray-800 rounded-tl-sm backdrop-blur-sm"
                    }`,
                },
                [
                  message.agent &&
                  React.createElement(
                    "div",
                    {
                      key: "agent",
                      className: `text-xs font-bold mb-1 uppercase tracking-wider ${message.role === "user" ? "text-blue-100" : "text-indigo-600"}`,
                    },
                    message.agent === "multiple"
                      ? `Multiple agents (${message.agentIds?.join(", ")})`
                      : message.agent
                  ),
                  React.createElement(
                    "div",
                    { key: "content", className: "whitespace-pre-wrap text-sm leading-relaxed" },
                    message.agent === "multiple"
                      ? // Split multi-agent response by agent name pattern and render each with border
                      (() => {
                        const lines = (message.content || "").split("\n");
                        const agents: any[] = [];
                        let currentAgent: string | null = null;
                        let currentText: string[] = [];

                        lines.forEach((line: string) => {
                          const agentMatch = line.match(/^(.*?):$/);
                          if (agentMatch && agentMatch[1].includes("Agent")) {
                            if (currentAgent) {
                              agents.push({ name: currentAgent, text: currentText.join("\n").trim() });
                            }
                            currentAgent = agentMatch[1];
                            currentText = [];
                          } else {
                            currentText.push(line);
                          }
                        });
                        if (currentAgent) {
                          agents.push({ name: currentAgent, text: currentText.join("\n").trim() });
                        }

                        return agents.map((agent, idx) =>
                          React.createElement(
                            "div",
                            {
                              key: `agent-${idx}`,
                              className: "mb-3 pb-3 border-b border-gray-200/50 last:border-b-0",
                            },
                            [
                              React.createElement(
                                "div",
                                { key: "name", className: "font-bold text-xs mb-1 text-gray-900" },
                                agent.name
                              ),
                              React.createElement(
                                "div",
                                { key: "text", className: "text-sm" },
                                agent.text
                              ),
                            ]
                          )
                        );
                      })()
                      : message.content
                  ),

                  // Replaced inline citations with CitationView
                  message.citations &&
                  message.citations.length > 0 &&
                  React.createElement(CitationView, { key: "citations", citations: message.citations }),

                  message.role === "assistant" &&
                  React.createElement(
                    "div",
                    {
                      key: "save-decision",
                      className: "mt-3 flex justify-end",
                    },
                    React.createElement(
                      "button",
                      {
                        onClick: () => onSaveDecision(message.content),
                        className:
                          "text-xs bg-green-50 text-green-700 px-3 py-1.5 rounded-full hover:bg-green-100 border border-green-200/50 flex items-center gap-1.5 transition-colors font-medium",
                      },
                      [
                        React.createElement("span", { key: "icon" }, "💾"),
                        "Save Decision"
                      ]
                    )
                  ),
                  // Action Panel Component
                  message.actionMeta &&
                  React.createElement(ActionPanel, { key: "action-panel", actionMeta: message.actionMeta }),
                ]
              )
            )
          ),
          loading &&
          React.createElement(
            "div",
            { key: "loading", className: "flex justify-start" },
            React.createElement(
              "div",
              { className: "bg-white/50 border border-white/60 rounded-2xl rounded-tl-sm px-4 py-2 text-sm text-gray-500 italic" },
              "Thinking..."
            )
          ),
          React.createElement("div", { key: "scroll-anchor" }),
        ]
      ),

      React.createElement(
        "div",
        {
          key: "input-area",
          className: "p-4 border-t border-white/40 bg-white/30 backdrop-blur-md rounded-b-[24px]"
        },
        [
          // Context selectors for agents
          React.createElement(
            "div",
            { key: "context-selectors", className: "mb-3 flex flex-wrap items-center gap-4" },
            [
              // Tone selector shown only for the Marketing agent
              activeAgent === "marketing" &&
              React.createElement(
                "div",
                { key: "tone-select", className: "flex items-center space-x-2" },
                [
                  React.createElement(
                    "label",
                    { key: "tone-label", className: "text-xs font-bold text-gray-600 uppercase tracking-widest" },
                    "Tone"
                  ),
                  React.createElement(
                    "select",
                    {
                      key: "tone-dropdown",
                      value: tone,
                      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setTone(e.target.value as any),
                      className: "border border-gray-200/60 rounded-lg px-2 py-1 text-sm bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20",
                    },
                    [
                      React.createElement("option", { key: "calm", value: "calm" }, "Calm (Professional)"),
                      React.createElement("option", { key: "warm", value: "warm" }, "Warm (Friendly)"),
                      React.createElement("option", { key: "firm", value: "firm" }, "Firm (Confident)"),
                    ]
                  ),
                ]
              ),
              // Length selector shown for all agents
              React.createElement(
                "div",
                { key: "length-select", className: "flex items-center space-x-2" },
                [
                  React.createElement(
                    "label",
                    { key: "length-label", className: "text-xs font-bold text-gray-600 uppercase tracking-widest" },
                    "Length"
                  ),
                  React.createElement(
                    "select",
                    {
                      key: "length-dropdown",
                      value: responseLength,
                      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setResponseLength(e.target.value as any),
                      className: "border border-gray-200/60 rounded-lg px-2 py-1 text-sm bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20",
                    },
                    [
                      React.createElement("option", { key: "short", value: "short" }, "Short"),
                      React.createElement("option", { key: "normal", value: "normal" }, "Normal"),
                    ]
                  ),
                ]
              ),
            ]
          ),

          React.createElement("div", { key: "input-row", className: "flex space-x-3 items-center" }, [
            React.createElement("input", {
              key: "input",
              type: "text",
              value: input,
              onChange: (e: any) => setInput(e.target.value),
              onKeyDown: (e: any) => e.key === "Enter" && sendMessage(),
              placeholder: routerMode
                ? "Ask any question..."
                : `Ask the ${activeAgent} agent...`,
              className:
                "flex-1 bg-white/60 border border-white/60 rounded-xl px-4 py-3 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition-all shadow-inner",
              disabled: loading,
            }),
            React.createElement(
              "button",
              {
                key: "send-btn",
                onClick: () => sendMessage(),
                disabled: loading || !input.trim(),
                className:
                  "bg-gradient-to-r from-blue-600 to-indigo-600 text-white w-12 h-12 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center transition-all transform hover:scale-105 active:scale-95",
              },
              React.createElement("span", { style: { fontSize: 20 } }, "➤")
            ),
          ]),
        ]
      ),
    ]
  );
}

// Agent Selector Component
// Agent Selector Component
function AgentSelector({
  activeAgent,
  onAgentChange,
  routerMode,
}: {
  activeAgent: AgentId;
  onAgentChange: (id: AgentId) => void;
  routerMode: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const agents = [
    { id: "chef", name: "Chef / Inventory", icon: "👨‍🍳", color: "bg-orange-100 text-orange-800", gradient: "linear-gradient(135deg, rgba(255,165,0,0.1) 0%, rgba(255,69,0,0.1) 100%)", border: "rgba(255,69,0,0.3)" },
    { id: "manager", name: "General Manager", icon: "📋", color: "bg-blue-100 text-blue-800", gradient: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(37,99,235,0.1) 100%)", border: "rgba(37,99,235,0.3)" },
    { id: "accountant", name: "Accountant", icon: "💰", color: "bg-green-100 text-green-800", gradient: "linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.1) 100%)", border: "rgba(5,150,105,0.3)" },
    { id: "marketing", name: "Marketing", icon: "📢", color: "bg-purple-100 text-purple-800", gradient: "linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(124,58,237,0.1) 100%)", border: "rgba(124,58,237,0.3)" },
  ] as const;

  const activeAgentDef = agents.find(a => a.id === activeAgent) || agents[0];

  const containerStyle: React.CSSProperties = {
    background: "rgba(255, 255, 255, 0.6)",
    borderRadius: 24,
    padding: 20,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.4)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    height: isExpanded ? "auto" : "80px", // Collapsed height ~80px (padding 20*2 + content)
    overflow: "hidden",
    cursor: isExpanded ? "default" : "pointer",
  };

  if (routerMode) {
    return React.createElement("div", { style: { ...containerStyle, height: "auto", cursor: "default" } }, [
      React.createElement("div", { key: "header", className: "flex items-center gap-2 mb-1" }, [
        React.createElement("div", { className: "w-2 h-2 rounded-full bg-green-500 animate-pulse" }),
        React.createElement("h3", { className: "font-semibold text-gray-900" }, "Router Mode Active")
      ]),
      React.createElement(
        "div",
        {
          key: "content",
          className: "p-4 rounded-2xl bg-gradient-to-br from-gray-50/50 to-white/50 border border-white/60 shadow-inner flex flex-col items-center text-center gap-2"
        },
        [
          React.createElement("span", { key: "icon", style: { fontSize: 32 } }, "🤖"),
          React.createElement(
            "p",
            { key: "description", className: "text-sm text-gray-600 font-medium" },
            "Your questions are automatically routed to the right agent."
          ),
        ]
      ),
    ]);
  }

  return React.createElement("div", {
    style: containerStyle,
    onClick: () => !isExpanded && setIsExpanded(true)
  }, [
    React.createElement("div", {
      key: "header",
      className: "flex justify-between items-center",
      onClick: (e: any) => {
        if (isExpanded) {
          e.stopPropagation();
          setIsExpanded(false);
        }
      },
      style: { cursor: "pointer", width: "100%" }
    }, [
      React.createElement("div", { key: "title-group", className: "flex items-center gap-3" }, [
        React.createElement("h3", { key: "title", className: "font-semibold text-gray-900 flex items-center gap-2" }, "Select Agent"),
        !isExpanded && React.createElement(
          "div",
          { key: "active-preview", className: "flex items-center gap-2 px-3 py-1 rounded-full bg-white/50 border border-white/60 text-sm text-gray-600" },
          [
            React.createElement("span", { key: "icon" }, activeAgentDef.icon),
            React.createElement("span", { key: "name" }, activeAgentDef.name),
          ]
        )
      ]),
      React.createElement("div", {
        key: "chevron",
        className: "text-gray-400 transition-transform duration-300",
        style: { transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }
      }, "▼")
    ]),
    React.createElement(
      "div",
      {
        key: "agents",
        className: "space-y-3",
        style: {
          opacity: isExpanded ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
          pointerEvents: isExpanded ? "auto" : "none"
        }
      },
      agents.map((agent) => {
        const isActive = activeAgent === agent.id;
        return React.createElement(
          "button",
          {
            key: agent.id,
            onClick: (e: any) => {
              e.stopPropagation();
              onAgentChange(agent.id as AgentId);
              setIsExpanded(false);
            },
            style: {
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              borderRadius: 16,
              border: isActive ? `1px solid ${agent.border}` : "1px solid rgba(255,255,255,0.5)",
              background: isActive ? agent.gradient : "rgba(255,255,255,0.4)",
              boxShadow: isActive ? `0 4px 12px ${agent.border.replace('0.3', '0.15')}` : "0 2px 4px rgba(0,0,0,0.02)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              transform: isActive ? "scale(1.02)" : "scale(1)",
            },
            onMouseEnter: (e: any) => !isActive && (e.currentTarget.style.background = "rgba(255,255,255,0.7)"),
            onMouseLeave: (e: any) => !isActive && (e.currentTarget.style.background = "rgba(255,255,255,0.4)"),
          },
          [
            React.createElement("div", { key: "left", className: "flex items-center gap-3" }, [
              React.createElement("span", { key: "icon", style: { fontSize: 24 } }, agent.icon),
              React.createElement("div", { key: "info", className: "text-left" }, [
                React.createElement(
                  "div",
                  { key: "name", className: `text-sm font-semibold ${isActive ? "text-gray-900" : "text-gray-700"}` },
                  agent.name
                ),
              ]),
            ]),
            isActive && React.createElement("div", {
              key: "indicator",
              className: "w-2 h-2 rounded-full bg-blue-500 shadow-sm"
            })
          ]
        );
      })
    ),
  ]);
}

// ---- Dashboard UI Components ----

function DecisionModal({
  isOpen,
  onClose,
  onSave,
  initialContext,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (decision: string, impact: string) => void;
  initialContext: string;
}) {
  const [decision, setDecision] = useState("");
  const [impact, setImpact] = useState("");

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setDecision("");
      setImpact("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return React.createElement(
    "div",
    {
      className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50",
      onClick: (e) => {
        if (e.target === e.currentTarget) onClose();
      },
    },
    React.createElement(
      "div",
      { className: "bg-white rounded-lg shadow-xl w-full max-w-lg p-6 m-4" },
      [
        React.createElement(
          "h3",
          { key: "title", className: "text-lg font-bold text-gray-900 mb-4" },
          "Save Decision"
        ),
        React.createElement(
          "div",
          { key: "context-area", className: "mb-4" },
          [
            React.createElement("label", { key: "l", className: "block text-sm font-medium text-gray-700 mb-1" }, "Context (from chat)"),
            React.createElement("div", { key: "v", className: "text-sm text-gray-600 bg-gray-50 p-2 rounded max-h-24 overflow-y-auto" }, initialContext),
          ]
        ),
        React.createElement(
          "div",
          { key: "decision-input", className: "mb-4" },
          [
            React.createElement("label", { key: "l", className: "block text-sm font-medium text-gray-700 mb-1" }, "Decision Made"),
            React.createElement("textarea", {
              key: "input",
              className: "w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none",
              rows: 3,
              placeholder: "What decision did you make based on this advice?",
              value: decision,
              onChange: (e: any) => setDecision(e.target.value),
            }),
          ]
        ),
        React.createElement(
          "div",
          { key: "impact-input", className: "mb-6" },
          [
            React.createElement("label", { key: "l", className: "block text-sm font-medium text-gray-700 mb-1" }, "Expected Impact"),
            React.createElement("input", {
              key: "input",
              type: "text",
              className: "w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none",
              placeholder: "e.g., Save £200/month, Improve service speed",
              value: impact,
              onChange: (e: any) => setImpact(e.target.value),
            }),
          ]
        ),
        React.createElement(
          "div",
          { key: "actions", className: "flex justify-end gap-3" },
          [
            React.createElement(
              "button",
              {
                key: "cancel",
                onClick: onClose,
                className: "px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg",
              },
              "Cancel"
            ),
            React.createElement(
              "button",
              {
                key: "save",
                onClick: () => {
                  if (!decision.trim()) return;
                  onSave(decision, impact);
                  onClose();
                },
                disabled: !decision.trim(),
                className: "px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed",
              },
              "Save Decision"
            ),
          ]
        ),
      ]
    )
  );
}

function DecisionsView({ decisions }: { decisions: any[] }) {
  return React.createElement(
    "div",
    { className: "h-full flex flex-col" },
    [
      React.createElement(
        "div",
        { key: "header", className: "mb-6 flex items-center justify-between" },
        [
          React.createElement("h2", { key: "title", className: "text-2xl font-bold text-gray-800" }, "Decision History"),
          React.createElement(
            "span",
            { key: "badge", className: "bg-white/50 border border-white/60 px-3 py-1 rounded-full text-sm font-medium text-gray-600 shadow-sm" },
            `${decisions.length} Record${decisions.length === 1 ? "" : "s"}`
          )
        ]
      ),
      decisions.length === 0
        ? React.createElement(
          "div",
          { key: "empty", className: "flex-1 flex flex-col items-center justify-center text-gray-500 opacity-60" },
          [
            React.createElement("span", { key: "icon", style: { fontSize: 48, marginBottom: 16 } }, "📝"),
            "No decisions recorded yet"
          ]
        )
        : React.createElement(
          "div",
          { key: "grid", className: "grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-min overflow-y-auto pb-6 pr-2" },
          decisions.map((decision, index) => {
            // Agent styling
            const agentEmoji = decision.agentType === 'chef' ? '👨‍🍳'
              : decision.agentType === 'manager' ? '👔'
                : decision.agentType === 'accountant' ? '💼'
                  : '🤖';

            const agentColor = decision.agentType === 'chef' ? 'bg-orange-100 text-orange-700'
              : decision.agentType === 'manager' ? 'bg-blue-100 text-blue-700'
                : decision.agentType === 'accountant' ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700';

            // Time ago
            const timeAgo = (() => {
              const now = new Date();
              const created = new Date(decision.timestamp);
              const diffMs = now.getTime() - created.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              if (diffMins < 1) return 'just now';
              if (diffMins < 60) return `${diffMins}m ago`;
              const diffHours = Math.floor(diffMins / 60);
              if (diffHours < 24) return `${diffHours}h ago`;
              const diffDays = Math.floor(diffHours / 24);
              return `${diffDays}d ago`;
            })();

            return React.createElement(
              "div",
              {
                key: index,
                className: "bg-white/80 backdrop-blur-sm border border-white/60 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all hover:scale-[1.01] flex flex-col gap-3",
              },
              [
                React.createElement(
                  "div",
                  { key: "header", className: "flex justify-between items-start" },
                  [
                    React.createElement(
                      "div",
                      { key: "agent", className: `flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${agentColor} text-xs font-bold uppercase tracking-wider` },
                      [
                        React.createElement("span", { key: "emoji" }, agentEmoji),
                        decision.agentType
                      ]
                    ),
                    React.createElement(
                      "span",
                      { key: "time", className: "text-xs text-gray-400 font-bold" },
                      timeAgo
                    )
                  ]
                ),
                React.createElement(
                  "div",
                  {
                    key: "decision-text",
                    className: "font-semibold text-gray-900 text-base leading-snug",
                  },
                  decision.decision
                ),
                React.createElement(
                  "div",
                  {
                    key: "footer",
                    className: "flex items-center gap-2 pt-3 border-t border-gray-100/50 mt-auto"
                  },
                  [
                    React.createElement("span", { key: "label", className: "text-xs text-gray-500 font-bold uppercase tracking-wide" }, "Impact"),
                    React.createElement(
                      "span",
                      { key: "impact", className: "text-sm text-gray-700 bg-gray-50/80 px-2 py-1 rounded-md border border-gray-100 truncate flex-1 font-medium" },
                      decision.expectedImpact
                    )
                  ]
                ),
              ]
            );
          })
        )
    ]
  );
}

function TargetsView({
  targets,
  runCheckForTarget,
  createNewTarget,
}: {
  targets: any[];
  runCheckForTarget: (t: any) => void;
  createNewTarget: () => void;
}) {
  return React.createElement(
    "div",
    { className: "h-full flex flex-col" },
    [
      React.createElement(
        "div",
        { key: "header", className: "mb-6 flex items-center justify-between" },
        [
          React.createElement(
            "div",
            { className: "flex items-center gap-3" },
            [
              React.createElement("h2", { key: "title", className: "text-2xl font-bold text-gray-800" }, "Active Targets"),
              React.createElement(
                "span",
                { key: "badge", className: "bg-white/50 border border-white/60 px-3 py-1 rounded-full text-sm font-medium text-gray-600 shadow-sm" },
                `${targets.length} Target${targets.length === 1 ? "" : "s"}`
              )
            ]
          ),
          React.createElement(
            "button",
            {
              key: "create-btn",
              onClick: createNewTarget,
              className: "flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold shadow-sm transition-all hover:scale-105 active:scale-95"
            },
            [
              React.createElement("span", { key: "icon", className: "text-lg" }, "+"),
              "New Target"
            ]
          )
        ]
      ),
      targets.length === 0
        ? React.createElement(
          "div",
          { key: "empty", className: "flex-1 flex flex-col items-center justify-center text-gray-500 opacity-60" },
          [
            React.createElement("span", { key: "icon", style: { fontSize: 48, marginBottom: 16 } }, "🎯"),
            "No targets set yet"
          ]
        )
        : React.createElement(
          "div",
          { key: "grid", className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-min overflow-y-auto pb-6 pr-2" },
          targets.map((t) => {
            // Time ago for last check
            const lastCheckedAgo = t.lastCheckedAt ? (() => {
              const now = new Date();
              const checked = new Date(t.lastCheckedAt);
              const diffMs = now.getTime() - checked.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              if (diffMins < 1) return 'just now';
              if (diffMins < 60) return `${diffMins}m ago`;
              const diffHours = Math.floor(diffMins / 60);
              if (diffHours < 24) return `${diffHours}h ago`;
              const diffDays = Math.floor(diffHours / 24);
              return `${diffDays}d ago`;
            })() : 'Never';

            // Status style
            const statusBadge = t.status === 'ok'
              ? { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '✓ OK', dot: 'bg-emerald-500' }
              : { bg: 'bg-red-100', text: 'text-red-700', label: '⚠ Alert', dot: 'bg-red-500' };

            return React.createElement(
              "div",
              {
                key: t.id,
                className: `bg-white/80 backdrop-blur-sm border ${t.status === 'alerting' ? 'border-red-500 ring-4 ring-red-500/10' : 'border-white/60'} p-6 rounded-2xl shadow-sm hover:shadow-md transition-all hover:scale-[1.01] flex flex-col gap-4 relative overflow-hidden`
              },
              [
                React.createElement(
                  "div",
                  { key: "header", className: "flex items-start justify-between" },
                  [
                    React.createElement(
                      "div",
                      { key: "info" },
                      [
                        React.createElement("h3", { key: "name", className: "font-bold text-gray-900 text-lg" }, t.name),
                        React.createElement("p", { key: "rule", className: "text-sm text-gray-500 mt-1" }, `${t.metric} ${t.direction === "below" ? "<" : ">"} ${t.threshold}`),
                      ]
                    ),
                    React.createElement(
                      "div",
                      {
                        key: "status",
                        className: `flex items-center gap-1.5 px-3 py-1 rounded-full ${statusBadge.bg} ${statusBadge.text} text-xs font-bold uppercase tracking-wider`
                      },
                      [
                        React.createElement("span", { key: "dot", className: `w-2 h-2 rounded-full ${statusBadge.dot}` }),
                        statusBadge.label
                      ]
                    ),
                  ]
                ),

                React.createElement(
                  "div",
                  {
                    key: "value-box",
                    className: `rounded-2xl p-4 border transition-all ${t.status === 'alerting' ? 'bg-red-50/50 border-red-100' : 'bg-gray-50/50 border-gray-100'}`
                  },
                  [
                    React.createElement(
                      "div",
                      { key: "header", className: "flex justify-between items-center mb-1" },
                      [
                        React.createElement("span", { key: "label", className: "text-[10px] text-gray-400 uppercase font-black tracking-widest" }, "Current Figure"),
                        t.lastValue !== undefined && React.createElement("span", {
                          key: "trend",
                          className: `text-[10px] font-bold px-2 py-0.5 rounded-full ${t.status === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`
                        }, t.status === 'ok' ? "HEALTHY" : "CRITICAL")
                      ]
                    ),
                    React.createElement(
                      "div",
                      { key: "val", className: "text-3xl font-black text-gray-900 tracking-tight" },
                      t.isChecking
                        ? React.createElement("span", { className: "text-blue-500 animate-pulse" }, "Running...")
                        : t.lastValue !== undefined
                          ? (t.metric.includes("pct") || t.metric.includes("rate") || t.metric.includes("margin") ? `${t.lastValue}%` : `£${t.lastValue.toLocaleString()}`)
                          : React.createElement("span", { className: "text-gray-300 italic text-xl font-medium" }, "Pending check...")
                    ),
                  ]
                ),

                React.createElement(
                  "div",
                  { key: "footer", className: "mt-auto pt-4 border-t border-gray-100/50 flex items-center justify-between gap-4" },
                  [
                    React.createElement(
                      "div",
                      { key: "meta", className: "text-xs text-gray-400 font-medium" },
                      [
                        React.createElement("div", { key: "check" }, `Last checked: ${lastCheckedAgo}`)
                      ]
                    ),
                    React.createElement(
                      "button",
                      {
                        key: "run",
                        disabled: t.isChecking,
                        onClick: () => runCheckForTarget(t),
                        className: `text-xs border px-3 py-2 rounded-lg font-bold transition-all shadow-sm flex items-center gap-2 ${t.isChecking ? 'bg-gray-50 border-gray-200 text-gray-400' : 'bg-white border-gray-200 hover:border-blue-400 text-gray-600 hover:text-blue-600'}`,
                      },
                      t.isChecking ? "Checking..." : "▶ Run Check"
                    ),
                  ]
                )
              ]
            );
          })
        )
    ]
  );
}

function NotificationsView({
  notifications,
  onMarkAllRead,
  onNotificationClick,
}: {
  notifications: any[];
  onMarkAllRead: () => void;
  onNotificationClick: (n: any) => void;
}) {
  return React.createElement(
    "div",
    { className: "h-full flex flex-col" },
    [
      React.createElement(
        "div",
        { key: "header", className: "mb-6 flex items-center justify-between" },
        [
          React.createElement(
            "div",
            { className: "flex items-center gap-3" },
            [
              React.createElement("h2", { key: "title", className: "text-2xl font-bold text-gray-800" }, "Notifications"),
              React.createElement(
                "span",
                { key: "badge", className: "bg-white/50 border border-white/60 px-3 py-1 rounded-full text-sm font-medium text-gray-600 shadow-sm" },
                `${notifications.filter(n => !n.read).length} Unread`
              )
            ]
          ),
          notifications.filter(n => !n.read).length > 0 &&
          React.createElement(
            "button",
            {
              key: "mark-all",
              onClick: onMarkAllRead,
              className: "text-blue-600 hover:text-blue-800 font-medium bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors",
            },
            "Mark all as read"
          ),
        ]
      ),
      notifications.length === 0
        ? React.createElement(
          "div",
          { key: "empty", className: "flex-1 flex flex-col items-center justify-center text-gray-500 opacity-60" },
          [
            React.createElement("span", { key: "icon", style: { fontSize: 48, marginBottom: 16 } }, "🔔"),
            "No notifications"
          ]
        )
        : React.createElement(
          "div",
          { key: "list", className: "space-y-4 auto-rows-min overflow-y-auto pb-6 pr-2" },
          notifications.map((n) => {
            // Determine severity color
            const severity = n.direction === 'below' && parseFloat(`${n.value}`) < parseFloat(`${n.threshold}`) * 0.8
              ? 'critical'
              : 'warning';
            const bgColor = n.read
              ? "bg-gray-50/50 border-gray-200/60"
              : severity === 'critical'
                ? "bg-red-50/80 border-red-200"
                : "bg-amber-50/80 border-amber-200";
            const borderColor = n.read ? "" : severity === 'critical' ? "border-l-4 border-l-red-500" : "border-l-4 border-l-amber-500";

            // Time ago calculation
            const timeAgo = (() => {
              const now = new Date();
              const created = new Date(n.createdAt);
              const diffMs = now.getTime() - created.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              if (diffMins < 1) return 'just now';
              if (diffMins < 60) return `${diffMins}m ago`;
              const diffHours = Math.floor(diffMins / 60);
              if (diffHours < 24) return `${diffHours}h ago`;
              const diffDays = Math.floor(diffHours / 24);
              return `${diffDays}d ago`;
            })();

            // Agent styling
            const agentEmoji = n.agentType === 'chef' ? '👨‍🍳'
              : n.agentType === 'manager' ? '👔'
                : n.agentType === 'accountant' ? '💼'
                  : '📢';

            return React.createElement(
              "div",
              {
                key: n.id,
                className: `border rounded-xl p-5 ${bgColor} ${borderColor} cursor-pointer hover:shadow-md transition-all duration-200 flex gap-4 items-start`,
                onClick: () => onNotificationClick(n)
              },
              [
                React.createElement("span", { key: "emoji", className: "text-2xl mt-1" }, agentEmoji),
                React.createElement(
                  "div",
                  { key: "content", className: "flex-1" },
                  [
                    React.createElement(
                      "div",
                      { key: "top", className: "flex justify-between items-start mb-1" },
                      [
                        React.createElement("h3", { key: "title", className: `font-bold text-gray-900 ${n.read ? "font-semibold" : "font-extrabold"}` }, n.targetName),
                        !n.read && React.createElement("span", { key: "dot", className: "w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" })
                      ]
                    ),
                    React.createElement("p", { key: "msg", className: "text-gray-700 text-sm mb-3" }, n.message),
                    React.createElement(
                      "div",
                      { key: "meta", className: "flex items-center gap-4 text-xs text-gray-500" },
                      [
                        React.createElement("span", { key: "time" }, timeAgo),
                        React.createElement("span", { key: "action", className: "text-blue-600 font-semibold flex items-center gap-1" }, ["View Issue", React.createElement("span", { key: "arrow" }, "→")])
                      ]
                    )
                  ]
                )
              ]
            );
          })
        )
    ]
  );
}

const QUICK_METRICS = [
  { name: "Daily Sales", key: "total_sales", icon: "💰", agent: "accountant" },
  { name: "Food Cost %", key: "food_cost_pct", icon: "🍱", agent: "chef" },
  { name: "Waste Rate", key: "waste_rate", icon: "🗑️", agent: "chef" },
  { name: "Staff Costs", key: "staff_costs", icon: "👥", agent: "manager" },
  { name: "Gross Margin", key: "gross_margin", icon: "📊", agent: "accountant" },
] as const;

function TargetModal({
  isOpen,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (target: {
    name: string;
    metric: string;
    threshold: number;
    direction: "above" | "below";
    agentType: AgentId;
  }) => void;
}) {
  const [name, setName] = useState("");
  const [metric, setMetric] = useState("");
  const [threshold, setThreshold] = useState("");
  const [direction, setDirection] = useState<"above" | "below">("below");
  // Default to accountant but allow user to pick
  const [agentType, setAgentType] = useState<AgentId>("accountant");

  useEffect(() => {
    if (isOpen) {
      setName("");
      setMetric("");
      setThreshold("");
      setDirection("below");
      setAgentType("accountant");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleQuickSelect = (qm: typeof QUICK_METRICS[number]) => {
    setName(qm.name);
    setMetric(qm.key);
    setAgentType(qm.agent as AgentId);
  };

  const summaryText = name && threshold
    ? `🔔 Alert when ${name} is ${direction === "above" ? "more than" : "less than"} ${metric.includes("pct") || metric.includes("rate") || metric.includes("margin") ? threshold + "%" : "£" + threshold}`
    : "Fill in the details to see your alert logic...";

  const agentIcons: Record<AgentId, string> = {
    chef: "👨‍🍳",
    manager: "📋",
    accountant: "📊",
    marketing: "📣",
  };

  return React.createElement(
    "div",
    {
      className: "fixed inset-0 flex items-center justify-center z-[100] px-4",
      onClick: (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
      },
    },
    [
      // Backdrop with blur
      React.createElement("div", {
        key: "backdrop",
        className: "absolute inset-0 bg-gray-900/40 backdrop-blur-md transition-opacity animate-in fade-in duration-300",
      }),

      // Modal Container
      React.createElement(
        "div",
        {
          key: "modal",
          className: "relative bg-white/90 backdrop-blur-xl rounded-[28px] shadow-2xl w-full max-w-lg overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300",
          style: { boxShadow: "0 25px 70px -10px rgba(0,0,0,0.3)" }
        },
        [
          // Header with Gradient
          React.createElement("div", {
            key: "header",
            className: "bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white"
          }, [
            React.createElement("h3", { key: "h", className: "text-2xl font-black tracking-tight" }, "Add Monitoring Target"),
            React.createElement("p", { key: "p", className: "text-blue-100 text-sm mt-1 font-medium" }, "Findsy AI will watch these metrics and alert you instantly.")
          ]),

          React.createElement("div", { key: "body", className: "p-8 space-y-6" }, [
            // Quick Select Section
            React.createElement("div", { key: "quick" }, [
              React.createElement("label", { className: "block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3" }, "Quick Start Metrics"),
              React.createElement("div", { className: "flex flex-wrap gap-2" },
                QUICK_METRICS.map(qm => React.createElement("button", {
                  key: qm.key,
                  onClick: () => handleQuickSelect(qm),
                  className: `px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${metric === qm.key ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`
                }, [
                  React.createElement("span", { key: "i" }, qm.icon),
                  qm.name
                ]))
              )
            ]),

            // Form Grid
            React.createElement("div", { key: "form", className: "space-y-4" }, [
              // Target Name
              React.createElement("div", { key: "name-row" }, [
                React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Target Name"),
                React.createElement("input", {
                  type: "text",
                  className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-semibold focus:ring-2 focus:ring-blue-500 transition-all",
                  placeholder: "e.g. Daily Revenue Safety Net",
                  value: name,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value),
                }),
              ]),

              // Logic Row
              React.createElement("div", { key: "logic", className: "grid grid-cols-12 gap-3 items-end" }, [
                React.createElement("div", { className: "col-span-12 md:col-span-5" }, [
                  React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Metric Key"),
                  React.createElement("input", {
                    type: "text",
                    className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-blue-500",
                    placeholder: "metric_id",
                    value: metric,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setMetric(e.target.value),
                  }),
                ]),
                React.createElement("div", { className: "col-span-12 md:col-span-4" }, [
                  React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Threshold"),
                  React.createElement("input", {
                    type: "number",
                    className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-blue-500",
                    placeholder: "0.00",
                    value: threshold,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setThreshold(e.target.value),
                  }),
                ]),
                React.createElement("div", { className: "col-span-12 md:col-span-3" }, [
                  React.createElement("select", {
                    className: "w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-blue-500 cursor-pointer",
                    value: direction,
                    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setDirection(e.target.value as "above" | "below"),
                  }, [
                    React.createElement("option", { key: "above", value: "above" }, "Above (>)"),
                    React.createElement("option", { key: "below", value: "below" }, "Below (<)"),
                  ]),
                ]),
              ]),

              // Agent Picker
              React.createElement("div", { key: "agent-row" }, [
                React.createElement("label", { className: "block text-sm font-bold text-gray-700 mb-1.5" }, "Assigned Expert"),
                React.createElement("div", { className: "grid grid-cols-4 gap-2" },
                  (Object.keys(AGENT_PERSONAS) as AgentId[]).map(id =>
                    React.createElement("button", {
                      key: id,
                      onClick: () => setAgentType(id),
                      className: `flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all ${agentType === id ? "border-blue-600 bg-blue-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`
                    }, [
                      React.createElement("span", { key: "i", className: "text-xl" }, agentIcons[id]),
                      React.createElement("span", { key: "n", className: "text-[10px] font-black uppercase tracking-tighter" }, id)
                    ])
                  )
                )
              ]),
            ]),

            // Live Summary Bubble
            React.createElement("div", {
              key: "summary",
              className: `p-4 rounded-2xl text-sm font-bold transition-all ${name && threshold ? "bg-indigo-600 text-white shadow-xl shadow-indigo-200" : "bg-gray-100 text-gray-400"}`
            }, summaryText),

            // Modal Footer
            React.createElement("div", { key: "actions", className: "flex gap-3 pt-2" }, [
              React.createElement("button", {
                key: "cancel",
                onClick: onClose,
                className: "flex-1 px-6 py-4 rounded-2xl text-sm font-black text-gray-500 hover:bg-gray-100 transition-colors uppercase tracking-widest",
              }, "Cancel"),
              React.createElement("button", {
                key: "create",
                onClick: () => {
                  if (!name || !metric || !threshold) return;
                  onSave({
                    name,
                    metric,
                    threshold: parseFloat(threshold),
                    direction,
                    agentType,
                  });
                  onClose();
                },
                className: "flex-[2] px-6 py-4 rounded-2xl text-sm font-black text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed",
                disabled: !name || !metric || !threshold
              }, "Create Target")
            ]),
          ]),
        ]
      )
    ]
  );
}


type DateFilterType = "yesterday" | "last-week" | "same-day-last-week" | "last-quarter" | "6-months" | "custom";

interface DateFilter {
  type: DateFilterType;
  startDate?: string;
  endDate?: string;
}

type Kpi = {
  title: string;
  value: string;
  delta?: string;
  icon: string;
  trend?: "up" | "down" | "flat";
  isMissing?: boolean;
  suggestion?: string;
  metricKey?: string;
};

function getDashboardKpis(filter: DateFilter, files: any[]): Kpi[] {
  const { type } = filter;

  // Try to find values in files
  const revenueVal = findMetricInFiles("total_sales", files);
  const coversVal = findMetricInFiles("covers", files);
  const aovVal = findMetricInFiles("aov", files);
  const marginVal = findMetricInFiles("gross_margin", files);

  // Fallback demo modifiers if not in files
  let revenue = revenueVal !== undefined ? revenueVal : 4520;
  let revenueDelta = 12;
  let coversStr = coversVal !== undefined ? `${coversVal}/140` : "125/140";
  let coversDelta = -11;
  let aov = aovVal !== undefined ? aovVal : 36.20;
  let aovDelta = 3;
  let margin = marginVal !== undefined ? marginVal : 64;
  let marginDelta = -2;

  // Modifiers based on type (only for demo values)
  if (revenueVal === undefined) {
    if (type === "yesterday") { revenue *= 0.85; revenueDelta = -4; }
    else if (type === "last-week") { revenue *= 1.15; revenueDelta = 15; }
  }

  const rangeTitle = type === "custom"
    ? `${filter.startDate || "?"} to ${filter.endDate || "?"}`
    : type.replace(/-/g, " ");

  return [
    {
      title: `Revenue (${rangeTitle})`,
      value: revenueVal !== undefined ? `£${Math.round(revenueVal).toLocaleString()}` : "Pending Data",
      delta: revenueVal !== undefined ? undefined : formatPercent(revenueDelta),
      icon: "💷",
      isMissing: revenueVal === undefined,
      suggestion: "Upload a POS Sales Report or Daily Revenue CSV",
      metricKey: "total_sales"
    },
    {
      title: "Covers vs expected",
      value: coversVal !== undefined ? `${coversVal}/140` : "Pending Data",
      delta: coversVal !== undefined ? undefined : formatPercent(coversDelta),
      icon: "🍽️",
      isMissing: coversVal === undefined,
      suggestion: "Upload a Guest Log or Booking Summary",
      metricKey: "covers"
    },
    {
      title: "Average order value",
      value: aovVal !== undefined ? `£${aovVal.toFixed(2)}` : "Pending Data",
      delta: aovVal !== undefined ? undefined : formatPercent(aovDelta),
      icon: "💰",
      isMissing: aovVal === undefined,
      suggestion: "Upload Transaction Receipts or receiver data",
      metricKey: "aov"
    },
    {
      title: "Gross margin",
      value: marginVal !== undefined ? `${marginVal}%` : "Pending Data",
      delta: marginVal !== undefined ? undefined : `${marginDelta > 0 ? "+" : ""}${marginDelta}pts`,
      icon: "📊",
      isMissing: marginVal === undefined,
      suggestion: "Upload an Inventory COGS sheet or P&L report",
      metricKey: "gross_margin"
    },
  ];
}

function formatPercent(n: number) {
  return `${n > 0 ? "+" : ""}${n}%`;
}

function Card({
  title,
  right,
  children,
  style = {} as React.CSSProperties,
}: {
  title?: string;
  right?: React.ReactNode;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const safeStyle = style || {};
  return React.createElement(
    "div",
    {
      style: {
        borderRadius: 24,
        border: "1px solid rgba(255,255,255,.4)",
        background: "linear-gradient(135deg, rgba(255,255,255,.95) 0%, rgba(255,255,255,.85) 100%)",
        boxShadow: "0 20px 60px rgba(0,0,0,.12), 0 8px 24px rgba(0,0,0,.06)",
        backdropFilter: "blur(20px)",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        ...safeStyle,
        padding: safeStyle.padding !== undefined ? safeStyle.padding : 20,
      },
    },
    [
      (title || right) &&
      React.createElement(
        "div",
        { key: "header", style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 4 } },
        [
          title
            ? React.createElement("div", { key: "title", style: { fontSize: 15, fontWeight: 900, color: "rgba(0,0,0,.85)", letterSpacing: "-0.3px" } }, title)
            : React.createElement("div", { key: "empty" }),
          right && React.createElement("div", { key: "right" }, right),
        ]
      ),
      React.createElement(
        "div",
        {
          key: "content",
          style: {
            marginTop: title || right ? 12 : 0,
            height: "100%",
            display: safeStyle.display || "block",
            flexDirection: safeStyle.flexDirection || "initial",
            gap: safeStyle.gap || "initial",
            flex: 1,
          }
        },
        children
      ),
    ]
  );
}

function KpiCard({ kpi, onExplain, onFillManual }: { kpi: Kpi; onExplain?: () => void; onFillManual?: () => void }) {
  const isPositive = kpi.delta && (kpi.delta.includes("+") || !kpi.delta.includes("-"));

  return React.createElement(
    Card,
    {
      style: {
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        height: 230,
        background: kpi.isMissing
          ? "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)",
        border: kpi.isMissing ? "2px dashed rgba(99,102,241,.25)" : "none",
      },
    },
    [
      React.createElement(
        "div",
        { key: "header", style: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, minHeight: 48 } },
        [
          React.createElement("div", { key: "title", style: { fontSize: 12, fontWeight: 700, color: "#64748b", lineHeight: 1.4, flex: 1, letterSpacing: "0.02em", textTransform: "uppercase" } }, kpi.title),
          React.createElement(
            "div",
            {
              key: "icon",
              style: {
                width: 44,
                height: 44,
                borderRadius: 12,
                display: "grid",
                placeItems: "center",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                boxShadow: "0 4px 14px rgba(99,102,241,.25)",
                flexShrink: 0,
              },
            },
            React.createElement("span", { style: { fontSize: 20 } }, kpi.icon)
          ),
        ]
      ),
      React.createElement("div", { key: "value", style: { display: "flex", alignItems: "baseline", gap: 12, marginTop: 4 } }, [
        React.createElement(
          "div",
          { key: "val", style: { fontSize: kpi.isMissing ? 24 : 36, fontWeight: 900, letterSpacing: "-0.02em", color: kpi.isMissing ? "#cbd5e1" : "#0f172a", lineHeight: 1 } },
          kpi.value
        ),
        kpi.delta && !kpi.isMissing &&
        React.createElement(
          "span",
          {
            key: "delta",
            style: {
              fontSize: 13,
              fontWeight: 700,
              padding: "6px 12px",
              borderRadius: 8,
              background: isPositive
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: 4,
            },
          },
          [
            React.createElement("span", { key: "arrow" }, isPositive ? "↗" : "↘"),
            kpi.delta
          ]
        ),
      ]),
      // Suggestion and Fill if missing
      kpi.isMissing && kpi.suggestion && React.createElement(
        "div",
        { key: "suggestion-box", style: { display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" } },
        [
          React.createElement(
            "div",
            { key: "msg", style: { fontSize: 11, color: "#6366f1", fontWeight: 600, padding: "10px 12px", background: "rgba(99,102,241,.08)", borderRadius: 8, display: "flex", gap: 8, alignItems: "start", lineHeight: 1.4 } },
            [React.createElement("span", { key: "i", style: { fontSize: 14, flexShrink: 0 } }, "💡"), kpi.suggestion]
          ),
          onFillManual && React.createElement(
            "button",
            {
              key: "fill-btn",
              onClick: onFillManual,
              style: {
                fontSize: 11,
                fontWeight: 700,
                color: "#ffffff",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                border: "none",
                padding: "10px 16px",
                borderRadius: 8,
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 2px 8px rgba(99,102,241,.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              },
            },
            [React.createElement("span", { key: "txt" }, "Add Manual Entry"), React.createElement("span", { key: "arrow" }, "→")]
          )
        ]
      ),
      // Ask agent anchor button
      !kpi.isMissing && onExplain &&
      React.createElement(
        "button",
        {
          key: "explain",
          onClick: onExplain,
          style: {
            marginTop: "auto",
            alignSelf: "stretch",
            background: "rgba(99,102,241,.06)",
            border: "1px solid rgba(99,102,241,.15)",
            borderRadius: 12,
            padding: "12px 14px",
            color: "#6366f1",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          },
          onMouseEnter: (e: any) => {
            e.currentTarget.style.background = "rgba(99,102,241,.1)";
            e.currentTarget.style.transform = "translateY(-1px)";
          },
          onMouseLeave: (e: any) => {
            e.currentTarget.style.background = "rgba(99,102,241,.06)";
            e.currentTarget.style.transform = "translateY(0)";
          },
        },
        [
          React.createElement("span", { key: "icon", style: { fontSize: 13 } }, "💬"),
          React.createElement("span", { key: "text" }, "Explain this metric"),
        ]
      ),
    ]
  );
}

function DateFilterSelector({ filter, onChange }: { filter: DateFilter; onChange: (f: DateFilter) => void }) {
  const [showCustom, setShowCustom] = useState(false);

  const quickFilters: { label: string; type: DateFilterType }[] = [
    { label: "vs Yesterday", type: "yesterday" },
    { label: "vs Last Week", type: "last-week" },
    { label: "vs Same Day Last Week", type: "same-day-last-week" },
  ];

  return React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 } }, [
    // Toggles
    React.createElement("div", { key: "toggles", style: { display: "flex", background: "rgba(0,0,0,.04)", padding: "4px", borderRadius: "12px", gap: "2px" } },
      quickFilters.map((q) => React.createElement("button", {
        key: q.type,
        onClick: () => { setShowCustom(false); onChange({ type: q.type }); },
        style: {
          padding: "6px 14px",
          borderRadius: "10px",
          fontSize: "12px",
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
          background: filter.type === q.type ? "#fff" : "transparent",
          color: filter.type === q.type ? "#000" : "rgba(0,0,0,.5)",
          boxShadow: filter.type === q.type ? "0 2px 8px rgba(0,0,0,.08)" : "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        }
      }, q.label))
    ),

    // Dropdown / More
    React.createElement("div", { key: "more", style: { display: "flex", alignItems: "center", gap: 8 } }, [
      React.createElement("select", {
        key: "select",
        value: ["last-quarter", "6-months", "custom"].includes(filter.type) ? filter.type : "default",
        onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
          const val = e.target.value as DateFilterType;
          if (val === "custom") {
            setShowCustom(true);
          } else {
            setShowCustom(false);
            onChange({ type: val });
          }
        },
        style: {
          padding: "8px 12px",
          borderRadius: "12px",
          fontSize: "13px",
          fontWeight: 600,
          border: "1px solid rgba(0,0,0,.08)",
          background: "#fff",
          cursor: "pointer",
          outline: "none",
        }
      }, [
        React.createElement("option", { key: "def", value: "default", disabled: true }, "Set range..."),
        React.createElement("option", { key: "lq", value: "last-quarter" }, "Last Quarter"),
        React.createElement("option", { key: "6m", value: "6-months" }, "6 Months"),
        React.createElement("option", { key: "cust", value: "custom" }, "Custom Range"),
      ]),
      showCustom && React.createElement(CustomDateRangePicker, {
        key: "picker",
        onClose: () => setShowCustom(false),
        onSelect: (start, end) => {
          setShowCustom(false);
          onChange({ type: "custom", startDate: start, endDate: end });
        }
      })
    ])
  ]);
}

function CustomDateRangePicker({ onSelect, onClose }: { onSelect: (start: string, end: string) => void; onClose: () => void }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  return React.createElement("div", { key: "picker-container" }, [
    // Backdrop
    React.createElement("div", {
      key: "backdrop",
      onClick: onClose,
      style: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,.3)",
        backdropFilter: "blur(4px)",
        zIndex: 999,
      }
    }),
    // Modal
    React.createElement("div", {
      key: "modal",
      style: {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        background: "#fff",
        padding: 24,
        borderRadius: 24,
        boxShadow: "0 25px 50px -12px rgba(0,0,0,.25)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 20,
        border: "1px solid rgba(255,255,255,.2)",
        width: 340,
      }
    }, [
      React.createElement("div", { key: "h", style: { display: "flex", justifyContent: "space-between", alignItems: "center" } }, [
        React.createElement("h3", { style: { margin: 0, fontSize: 18, fontWeight: 900, letterSpacing: "-0.5px" } }, "Select Range"),
        React.createElement("button", { onClick: onClose, style: { border: "none", background: "rgba(0,0,0,.05)", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", fontSize: 16 } }, "×")
      ]),
      React.createElement("div", { key: "inputs", style: { display: "flex", flexDirection: "column", gap: 14 } }, [
        React.createElement("div", { key: "s" }, [
          React.createElement("label", { style: { display: "block", fontSize: 11, fontWeight: 800, color: "rgba(0,0,0,.5)", marginBottom: 6, letterSpacing: "0.5px" } }, "START DATE"),
          React.createElement("input", { type: "date", value: start, onChange: (e: any) => setStart(e.target.value), style: { width: "100%", padding: 12, borderRadius: 14, border: "1px solid rgba(0,0,0,.08)", background: "rgba(0,0,0,.02)", fontSize: 14, fontWeight: 600 } })
        ]),
        React.createElement("div", { key: "e" }, [
          React.createElement("label", { style: { display: "block", fontSize: 11, fontWeight: 800, color: "rgba(0,0,0,.5)", marginBottom: 6, letterSpacing: "0.5px" } }, "END DATE"),
          React.createElement("input", { type: "date", value: end, onChange: (e: any) => setEnd(e.target.value), style: { width: "100%", padding: 12, borderRadius: 14, border: "1px solid rgba(0,0,0,.08)", background: "rgba(0,0,0,.02)", fontSize: 14, fontWeight: 600 } })
        ]),
      ]),
      React.createElement("button", {
        key: "btn",
        onClick: () => onSelect(start, end),
        style: {
          background: "linear-gradient(135deg, #000 0%, #333 100%)",
          color: "#fff",
          padding: "14px",
          borderRadius: "14px",
          fontWeight: 800,
          border: "none",
          cursor: "pointer",
          boxShadow: "0 10px 15px -3px rgba(0,0,0,.1)",
          marginTop: 8,
        }
      }, "Apply Selection")
    ])
  ]);
}

function DashboardView({ onKpiExplain, onFillManual, uploadedFiles }: { onKpiExplain?: (kpi: Kpi) => void; onFillManual?: (kpi: Kpi) => void; uploadedFiles: any[] }) {
  const [dateFilter, setDateFilter] = useState<DateFilter>({ type: "last-week" });
  const kpis = getDashboardKpis(dateFilter, uploadedFiles);

  // Demand heatmap data (7 days x 24 hours)
  const heatmapDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const heatmapHours = ["6am", "9am", "12pm", "3pm", "6pm", "9pm"];
  // Sample data: higher values = more covers
  const heatmapData = [
    [10, 15, 45, 30, 60, 55], // Mon
    [12, 18, 42, 28, 58, 52], // Tue
    [11, 16, 48, 32, 65, 58], // Wed
    [13, 20, 50, 35, 70, 62], // Thu
    [15, 25, 55, 40, 80, 75], // Fri
    [20, 35, 65, 50, 90, 85], // Sat
    [18, 30, 60, 45, 75, 70], // Sun
  ];

  // Cost pressure data
  const costLabels = ["Week 1", "Week 2", "Week 3", "Week 4"];
  const revenueData = [4200, 4350, 4520, 4480];
  const costData = [3200, 3400, 3650, 3800];

  // Warnings data
  const warnings = [
    { id: 1, severity: "red", title: "Staff costs up 15%", reason: "Overtime trending above forecast" },
    { id: 2, severity: "amber", title: "Inventory wastage high", reason: "Spoilage rate 8% vs 5% target" },
    { id: 3, severity: "amber", title: "Slow-moving items", reason: "3 menu items <5 orders/week" },
  ];

  // Recommended actions
  const actions = [
    { id: 1, title: "Adjust Friday staffing", rationale: "Reduce by 2 FTE based on demand curve" },
    { id: 2, title: "Review supplier contracts", rationale: "Ingredient costs up 12% vs market" },
    { id: 3, title: "Promote high-margin items", rationale: "Boost pasta dishes (68% margin)" },
  ];

  return React.createElement("div", { style: { display: "grid", gap: 20 } }, [
    // Header with Date Filter
    React.createElement(DateFilterSelector, { key: "filter", filter: dateFilter, onChange: setDateFilter }),

    // Row 1: KPI cards (4 widgets)
    React.createElement(
      "div",
      { key: "kpis", style: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16 } },
      kpis.map((k) => React.createElement(KpiCard, {
        key: k.title,
        kpi: k,
        onExplain: onKpiExplain ? () => onKpiExplain(k) : undefined,
        onFillManual: onFillManual ? () => onFillManual(k) : undefined
      }))
    ),

    // Row 2: Charts (2 widgets)
    React.createElement("div", { key: "charts-row", style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 } }, [
      // Widget 5: Demand heatmap
      React.createElement(
        Card,
        { key: "heatmap", title: "Demand Heatmap" },
        React.createElement("div", { style: { padding: "10px 0" } }, [
          React.createElement("div", { key: "grid", style: { display: "grid", gridTemplateColumns: "60px repeat(6, 1fr)", gap: 4, fontSize: 11 } }, [
            // Header row
            React.createElement("div", { key: "corner" }),
            ...heatmapHours.map((h, i) =>
              React.createElement("div", { key: `h-${i}`, style: { textAlign: "center", fontWeight: 800, color: "rgba(0,0,0,.6)" } }, h)
            ),
            // Data rows
            ...heatmapDays.flatMap((day, dayIdx) => [
              React.createElement("div", { key: `day-${dayIdx}`, style: { fontWeight: 800, color: "rgba(0,0,0,.7)", paddingRight: 8 } }, day),
              ...heatmapData[dayIdx].map((val, hourIdx) => {
                const intensity = Math.min(val / 90, 1);
                // Dynamic HSL gradient - optimized for vibrant indigo to purple
                const hue = 230 + (intensity * 50); // 230 (Indigo) -> 280 (Purple)
                const saturation = 80 + (intensity * 20); // Maintain high saturation
                const lightness = 93 - (intensity * 40); // 93% (Very Light) -> 53% (Vibrant)
                const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
                const textColor = intensity > 0.5 ? "#fff" : "rgba(0,0,0,.85)";

                return React.createElement(
                  "div",
                  {
                    key: `cell-${dayIdx}-${hourIdx}`,
                    style: {
                      background: bgColor,
                      borderRadius: 8,
                      padding: "10px 4px",
                      textAlign: "center",
                      fontWeight: 900,
                      color: textColor,
                      boxShadow: intensity > 0.6 ? "0 4px 12px rgba(99,102,241,.25)" : "0 2px 6px rgba(0,0,0,.08)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                    },
                    onMouseEnter: (e: any) => {
                      e.currentTarget.style.transform = "scale(1.1)";
                      e.currentTarget.style.zIndex = "10";
                      e.currentTarget.style.boxShadow = "0 8px 20px rgba(99,102,241,.35)";
                    },
                    onMouseLeave: (e: any) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.zIndex = "1";
                      e.currentTarget.style.boxShadow = intensity > 0.6 ? "0 4px 12px rgba(99,102,241,.25)" : "0 2px 6px rgba(0,0,0,.08)";
                    },
                  },
                  val
                );
              }),
            ]),
          ]),
        ])
      ),

      // Widget 6: Cost pressure signals
      React.createElement(
        Card,
        { key: "cost-pressure", title: "Cost Pressure Signals" },
        React.createElement("div", { style: { padding: "20px 0 10px 0", height: 300 } }, [
          React.createElement(
            "svg",
            { key: "svg", width: "100%", height: "100%", viewBox: "0 0 500 240", style: { overflow: "visible" } },
            [
              // Gradient definitions
              React.createElement(
                "defs",
                { key: "defs" },
                [
                  React.createElement(
                    "linearGradient",
                    { key: "revenueGradient", id: "revenueGradient", x1: "0%", y1: "0%", x2: "0%", y2: "100%" },
                    [
                      React.createElement("stop", { key: "stop1", offset: "0%", stopColor: "rgb(16,185,129)", stopOpacity: 0.35 }),
                      React.createElement("stop", { key: "stop2", offset: "90%", stopColor: "rgb(16,185,129)", stopOpacity: 0.02 }),
                    ]
                  ),
                  React.createElement(
                    "linearGradient",
                    { key: "costGradient", id: "costGradient", x1: "0%", y1: "0%", x2: "0%", y2: "100%" },
                    [
                      React.createElement("stop", { key: "stop1", offset: "0%", stopColor: "rgb(239,68,68)", stopOpacity: 0.3 }),
                      React.createElement("stop", { key: "stop2", offset: "90%", stopColor: "rgb(239,68,68)", stopOpacity: 0.02 }),
                    ]
                  ),
                  // Drop shadow filter for lines
                  React.createElement("filter", { key: "glow", id: "glow", x: "-20%", y: "-20%", width: "140%", height: "140%" }, [
                    React.createElement("feGaussianBlur", { key: "blur", stdDeviation: "3", result: "blur" }),
                    React.createElement("feComposite", { key: "composite", in: "SourceGraphic", in2: "blur", operator: "over" })
                  ]),
                ]
              ),

              // Chart Area Background
              React.createElement("rect", { key: "chart-bg", x: 40, y: 40, width: 440, height: 160, fill: "rgba(248,250,252,0.5)", rx: 4 }),

              // Advanced Legend (Top aligned, wider spread)
              React.createElement("g", { key: "legend", transform: "translate(40, 0)" }, [
                React.createElement("rect", {
                  key: "legend-bg",
                  x: 0,
                  y: 0,
                  width: 280,
                  height: 30,
                  rx: 6,
                  fill: "rgba(255,255,255,.9)",
                  stroke: "rgba(0,0,0,.04)",
                }),
                React.createElement("circle", { key: "rev-dot", cx: 16, cy: 15, r: 4, fill: "rgb(16,185,129)" }),
                React.createElement("text", { key: "rev-label", x: 28, y: 19, fontSize: 12, fontWeight: 700, fill: "rgba(0,0,0,.7)" }, "Revenue (Forecast)"),
                React.createElement("circle", { key: "cost-dot", cx: 165, cy: 15, r: 4, fill: "rgb(239,68,68)" }),
                React.createElement("text", { key: "cost-label", x: 177, y: 19, fontSize: 12, fontWeight: 700, fill: "rgba(0,0,0,.7)" }, "Costs (Actual)"),
              ]),

              // Horizontal Grid lines (5 lines)
              ...Array.from({ length: 5 }).map((_, i) => {
                const y = 40 + (i * 40);
                return React.createElement("g", { key: `hgrid-${i}` }, [
                  React.createElement("line", {
                    x1: 40,
                    y1: y,
                    x2: 480,
                    y2: y,
                    stroke: "rgba(0,0,0,.05)",
                    strokeWidth: 1,
                  }),
                  // Y-axis labels
                  React.createElement("text", {
                    x: 32,
                    y: y + 4,
                    textAnchor: "end",
                    fontSize: 10,
                    fontWeight: 500,
                    fill: "rgba(0,0,0,.4)"
                  }, `£${4500 - (i * 500)}`)
                ]);
              }),

              // Vertical Grid lines (for weeks)
              ...Array.from({ length: 4 }).map((_, i) => {
                const x = 40 + (i * 146.6); // 440 width / 3 segments = ~146.6
                return React.createElement("line", {
                  key: `vgrid-${i}`,
                  x1: x,
                  y1: 40,
                  x2: x,
                  y2: 200,
                  stroke: "rgba(0,0,0,.03)",
                  strokeWidth: 1,
                });
              }),

              // Revenue area fill
              React.createElement("path", {
                key: "revenue-area",
                // Adjusted scaling for new height (160px plot area) and width (440px)
                d: `M 40,${200 - ((revenueData[0] - 3000) / 10)} ${revenueData.map((v, i) => `L ${40 + i * 146.6},${200 - ((v - 3000) / 10)}`).join(" ")} L 480,200 L 40,200 Z`,
                fill: "url(#revenueGradient)",
              }),

              // Cost area fill
              React.createElement("path", {
                key: "cost-area",
                d: `M 40,${200 - ((costData[0] - 3000) / 10)} ${costData.map((v, i) => `L ${40 + i * 146.6},${200 - ((v - 3000) / 10)}`).join(" ")} L 480,200 L 40,200 Z`,
                fill: "url(#costGradient)",
              }),

              // Revenue line
              React.createElement("polyline", {
                key: "revenue-line",
                points: revenueData.map((v, i) => `${40 + i * 146.6},${200 - ((v - 3000) / 10)}`).join(" "),
                fill: "none",
                stroke: "rgb(16,185,129)",
                strokeWidth: 4,
                strokeLinecap: "round",
                strokeLinejoin: "round",
                filter: "url(#glow)",
              }),

              // Cost line
              React.createElement("polyline", {
                key: "cost-line",
                points: costData.map((v, i) => `${40 + i * 146.6},${200 - ((v - 3000) / 10)}`).join(" "),
                fill: "none",
                stroke: "rgb(239,68,68)",
                strokeWidth: 4,
                strokeLinecap: "round",
                strokeLinejoin: "round",
                filter: "url(#glow)",
              }),

              // Revenue data points
              ...revenueData.map((v, i) =>
                React.createElement("circle", {
                  key: `rev-point-${i}`,
                  cx: 40 + i * 146.6,
                  cy: 200 - ((v - 3000) / 10),
                  r: 6,
                  fill: "rgb(16,185,129)",
                  stroke: "white",
                  strokeWidth: 2.5,
                  style: { filter: "drop-shadow(0 2px 4px rgba(16,185,129,.3))", cursor: "pointer", transition: "all 0.2s ease" },
                  onMouseEnter: (e: any) => { e.currentTarget.setAttribute("r", "8"); },
                  onMouseLeave: (e: any) => { e.currentTarget.setAttribute("r", "6"); },
                })
              ),

              // Cost data points
              ...costData.map((v, i) =>
                React.createElement("circle", {
                  key: `cost-point-${i}`,
                  cx: 40 + i * 146.6,
                  cy: 200 - ((v - 3000) / 10),
                  r: 6,
                  fill: "rgb(239,68,68)",
                  stroke: "white",
                  strokeWidth: 2.5,
                  style: { filter: "drop-shadow(0 2px 4px rgba(239,68,68,.3))", cursor: "pointer", transition: "all 0.2s ease" },
                  onMouseEnter: (e: any) => { e.currentTarget.setAttribute("r", "8"); },
                  onMouseLeave: (e: any) => { e.currentTarget.setAttribute("r", "6"); },
                })
              ),

              // X-axis labels
              ...costLabels.map((label, i) =>
                React.createElement(
                  "text",
                  {
                    key: `label-${i}`,
                    x: 40 + i * 146.6,
                    y: 220,
                    textAnchor: "middle",
                    fontSize: 12,
                    fontWeight: 600,
                    fill: "rgba(0,0,0,.6)",
                  },
                  label
                )
              ),
            ]
          ),
        ])
      ),
    ]),

    // Row 3: Compact cards (2 widgets)
    React.createElement("div", { key: "compact-row", style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 } }, [
      // Widget 7: Warnings
      React.createElement(
        Card,
        { key: "warnings", title: "Warnings" },
        React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 12 } }, [
          ...warnings.map((w) =>
            React.createElement("div", { key: w.id, style: { display: "flex", alignItems: "flex-start", gap: 10 } }, [
              React.createElement(
                "span",
                {
                  key: "pill",
                  style: {
                    display: "inline-block",
                    padding: "4px 10px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 900,
                    background: w.severity === "red" ? "rgba(239,68,68,.15)" : "rgba(251,191,36,.15)",
                    color: w.severity === "red" ? "rgb(220,38,38)" : "rgb(217,119,6)",
                    border: w.severity === "red" ? "1px solid rgba(239,68,68,.3)" : "1px solid rgba(251,191,36,.3)",
                  },
                },
                w.severity.toUpperCase()
              ),
              React.createElement("div", { key: "content", style: { flex: 1 } }, [
                React.createElement("div", { key: "title", style: { fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,.8)", marginBottom: 4 } }, w.title),
                React.createElement("div", { key: "reason", style: { fontSize: 12, color: "rgba(0,0,0,.6)" } }, w.reason),
              ]),
              React.createElement(
                "button",
                {
                  key: "explain",
                  onClick: () => {
                    if (onKpiExplain) {
                      onKpiExplain({ title: w.title, value: "", icon: "⚠️" });
                    }
                  },
                  style: {
                    background: "none",
                    border: "none",
                    color: "rgba(59,130,246,.9)",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    padding: "4px 0",
                    textDecoration: "none",
                  },
                  onMouseEnter: (e: any) => {
                    e.target.style.textDecoration = "underline";
                  },
                  onMouseLeave: (e: any) => {
                    e.target.style.textDecoration = "none";
                  },
                },
                "Explain this"
              ),
            ])
          ),
        ])
      ),

      // Widget 8: Recommended actions
      React.createElement(
        Card,
        { key: "actions", title: "Recommended Actions" },
        React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 12 } }, [
          ...actions.map((a) =>
            React.createElement("div", { key: a.id, style: { display: "flex", flexDirection: "column", gap: 6 } }, [
              React.createElement("div", { key: "header", style: { display: "flex", justifyContent: "space-between", alignItems: "center" } }, [
                React.createElement("div", { key: "title", style: { fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,.8)" } }, a.title),
                React.createElement(
                  "button",
                  {
                    key: "ask",
                    onClick: () => {
                      if (onKpiExplain) {
                        onKpiExplain({ title: `Action: ${a.title}. Explain why, expected impact, and how to implement. Include confidence`, value: "", icon: "🎯" });
                      }
                    },
                    style: {
                      background: "none",
                      border: "none",
                      color: "rgba(59,130,246,.9)",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      padding: "4px 0",
                      textDecoration: "none",
                    },
                    onMouseEnter: (e: any) => {
                      e.target.style.textDecoration = "underline";
                    },
                    onMouseLeave: (e: any) => {
                      e.target.style.textDecoration = "none";
                    },
                  },
                  "Ask agent"
                ),
              ]),
              React.createElement("div", { key: "rationale", style: { fontSize: 12, color: "rgba(0,0,0,.6)" } }, a.rationale),
            ])
          ),
        ])
      ),
    ]),
  ]);
}

function AuthView({ onAuthSuccess }: { onAuthSuccess: (user: any) => void }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [venueName, setVenueName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Clear form fields when switching modes to prevent data transfer between Login/Register
  useEffect(() => {
    setEmail("");
    setPassword("");
    setFullName("");
    setVenueName("");
    setError(null);
    setResetSuccess(false);
  }, [isLogin, forgotMode]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (forgotMode) {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
        setResetSuccess(true);
      } else if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        onAuthSuccess(data.user);
      } else {
        // Sign up
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              venue_name: venueName,
            }
          }
        });
        if (error) throw error;

        if (data.user) {
          onAuthSuccess(data.user);
        }
      }
    } catch (err: any) {
      setError(err.message || "An error occurred during authentication");
    } finally {
      setLoading(false);
    }
  };

  return React.createElement("div", {
    className: "min-h-screen flex items-center justify-center p-4",
    style: {
      background: "radial-gradient(1400px 700px at 30% 10%, rgba(99,102,241,.15) 0%, rgba(255,255,255,0) 60%), radial-gradient(1000px 600px at 75% 40%, rgba(139,92,246,.1) 0%, rgba(255,255,255,0) 55%), #f9fafb",
    }
  }, [
    React.createElement("div", {
      key: "auth-card",
      className: "w-full max-w-md bg-white/70 backdrop-blur-xl border border-white/40 shadow-2xl rounded-[32px] overflow-hidden p-8",
    }, [
      // Logo & Header
      React.createElement("div", { key: "header", className: "text-center mb-8" }, [
        React.createElement("div", {
          key: "logo-container",
          className: "mb-4 flex items-center justify-center",
        }, React.createElement("img", {
          key: "logo",
          src: "/logo.png",
          alt: "Findsy",
          className: "h-12 w-auto",
        })),
        React.createElement("h1", { key: "title", className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, forgotMode ? "Reset Password" : "Findsy"),
        React.createElement("p", { key: "sub", className: "text-gray-500 font-medium px-4" },
          forgotMode
            ? "Enter your email to receive a reset link"
            : "Turn data into insights. Understand your business."
        ),
      ]),

      // Tabs
      !forgotMode ? React.createElement("div", {
        key: "tabs",
        className: "flex bg-gray-100/50 p-1 rounded-xl mb-8 border border-gray-200/20",
      }, [
        React.createElement("button", {
          key: "login-tab",
          onClick: () => setIsLogin(true),
          className: `flex-1 py-2 rounded-lg text-sm font-bold transition-all ${isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`
        }, "Login"),
        React.createElement("button", {
          key: "signup-tab",
          onClick: () => setIsLogin(false),
          className: `flex-1 py-2 rounded-lg text-sm font-bold transition-all ${!isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`
        }, "Register Venue"),
      ]) : null,

      // Form
      React.createElement("form", { key: "form", onSubmit: handleAuth, className: "space-y-4" }, [
        !isLogin && React.createElement("div", { key: "name-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Full Name"),
          React.createElement("input", {
            required: true,
            value: fullName,
            onChange: (e: any) => setFullName(e.target.value),
            placeholder: "Gordon Ramsay",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        !isLogin && React.createElement("div", { key: "venue-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Venue Name"),
          React.createElement("input", {
            required: true,
            value: venueName,
            onChange: (e: any) => setVenueName(e.target.value),
            placeholder: "The Lucky Fig Bakery",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        React.createElement("div", { key: "email-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Work Email"),
          React.createElement("input", {
            type: "email",
            required: true,
            value: email,
            onChange: (e: any) => setEmail(e.target.value),
            placeholder: "chef@venue.com",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        !forgotMode && React.createElement("div", { key: "password-field" }, [
          React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1" }, "Password"),
          React.createElement("input", {
            type: "password",
            required: true,
            value: password,
            onChange: (e: any) => setPassword(e.target.value),
            placeholder: "••••••••",
            className: "w-full bg-white/50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all",
          })
        ]),

        isLogin && !forgotMode && React.createElement("div", { key: "forgot-link", className: "text-right" }, [
          React.createElement("button", {
            key: "forgot-btn",
            type: "button",
            onClick: () => setForgotMode(true),
            className: "text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:text-indigo-700 transition-colors"
          }, "Forgot Password?")
        ]),

        forgotMode && resetSuccess && React.createElement("div", {
          key: "reset-success",
          className: "bg-green-50 text-green-600 text-xs font-bold p-4 rounded-xl border border-green-100 mb-2"
        }, "Password reset link sent! Please check your inbox (including spam)."),

        error && React.createElement("div", {
          key: "error",
          className: "bg-red-50 text-red-600 text-xs font-bold p-3 rounded-xl border border-red-100 animate-shake"
        }, error),

        React.createElement("button", {
          key: "submit",
          disabled: loading,
          className: "w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-indigo-200 transition-all active:scale-[0.98] mt-2 flex items-center justify-center gap-2",
        }, loading ? "Connecting..." : [
          forgotMode ? "Send Reset Link" : isLogin ? "Sign In" : "Create My Account",
          React.createElement("span", { key: "arrow" }, "→")
        ]),

        React.createElement("p", {
          key: "switch",
          className: "text-center text-xs text-gray-400 font-medium mt-6"
        }, [
          forgotMode
            ? "Remembered your password? "
            : isLogin ? "Don't have an account? " : "Already using Findsy? ",
          React.createElement("button", {
            key: "toggle",
            type: "button",
            onClick: () => {
              if (forgotMode) {
                setForgotMode(false);
                setResetSuccess(false);
              } else {
                setIsLogin(!isLogin);
              }
            },
            className: "text-indigo-600 font-bold hover:underline"
          }, forgotMode ? "Back to Login" : isLogin ? "Register your venue" : "Sign in here")
        ])
      ])
    ])
  ]);
}

function SettingsView({ user, venueName, onUpdate }: { user: any; venueName: string; onUpdate: () => void }) {
  const [fullName, setFullName] = useState("");
  const [vName, setVName] = useState(venueName);
  const [businessType, setBusinessType] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [venueId, setVenueId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    const load = async () => {
      const data = await supaFetchVenueProfile(user.id) as any;
      if (data) {
        setFullName(data.full_name || "");
        setVName(data.venues?.name || "");
        setBusinessType(data.venues?.business_type || "");
        setVenueId(data.venues?.id || "");
      }
      setLoading(false);
    };
    load();
  }, [user.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await supaUpdateVenueProfile(user.id, venueId, {
        fullName,
        venueName: vName,
        businessType
      });
      alert("Settings saved successfully!");
      onUpdate();
    } catch (e: any) {
      alert("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword) {
      alert("Please enter a new password.");
      return;
    }
    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      alert("Password updated successfully!");
      setNewPassword("");
    } catch (e: any) {
      alert("Failed to update password: " + e.message);
    } finally {
      setUpdatingPassword(false);
    }
  };

  if (loading) return React.createElement("div", { className: "p-8 text-center text-gray-500 font-medium" }, "Loading settings...");

  return React.createElement("div", { className: "max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" }, [
    React.createElement("div", { key: "header", className: "mb-8 text-center" }, [
      React.createElement("h2", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Venue Settings"),
      React.createElement("p", { className: "text-gray-500 font-medium" }, "Manage your restaurant's profile and preferences"),
    ]),

    React.createElement("div", { key: "form-container", className: "bg-white/70 backdrop-blur-xl border border-white/40 shadow-xl rounded-[32px] overflow-hidden p-8 space-y-6" }, [
      // Individual Fields
      React.createElement("div", { key: "name-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Owner/Manager Name"),
        React.createElement("input", {
          value: fullName,
          onChange: (e: any) => setFullName(e.target.value),
          className: "w-full bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium",
          placeholder: "e.g. Gordon Ramsay"
        })
      ]),

      React.createElement("div", { key: "venue-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Restaurant Name"),
        React.createElement("input", {
          value: vName,
          onChange: (e: any) => setVName(e.target.value),
          className: "w-full bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium",
          placeholder: "e.g. The Lucky Fig"
        })
      ]),

      React.createElement("div", { key: "type-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Business Type"),
        React.createElement("select", {
          value: businessType,
          onChange: (e: any) => setBusinessType(e.target.value),
          className: "w-full bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium appearance-none",
        }, [
          React.createElement("option", { key: "opt1", value: "" }, "Select business type..."),
          React.createElement("option", { key: "opt2", value: "Fine Dining" }, "Fine Dining"),
          React.createElement("option", { key: "opt3", value: "Casual Dining" }, "Casual Dining"),
          React.createElement("option", { key: "opt4", value: "Quick Service" }, "Quick Service / Fast Food"),
          React.createElement("option", { key: "opt5", value: "Bakery / Cafe" }, "Bakery / Cafe"),
          React.createElement("option", { key: "opt6", value: "Bar / Pub" }, "Bar / Pub"),
          React.createElement("option", { key: "opt7", value: "Dark Kitchen" }, "Dark Kitchen"),
        ])
      ]),

      React.createElement("div", { key: "email-field", className: "opacity-50 pointer-events-none" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "Account Email"),
        React.createElement("input", {
          value: user.email,
          readOnly: true,
          className: "w-full bg-gray-100/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm font-medium",
        })
      ]),

      React.createElement("button", {
        key: "save-btn",
        onClick: handleSave,
        disabled: saving,
        className: "w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-black py-5 rounded-2xl shadow-xl shadow-indigo-100 transition-all active:scale-[0.98] mt-4 flex items-center justify-center gap-3 text-sm uppercase tracking-widest",
      }, saving ? "Saving Details..." : [
        React.createElement("span", { key: "icon" }, "💾"),
        "Save All Changes"
      ])
    ]),

    React.createElement("div", { key: "password-card", className: "bg-white/70 backdrop-blur-xl border border-white/40 shadow-xl rounded-[32px] p-8 space-y-6" }, [
      React.createElement("div", { key: "header" }, [
        React.createElement("h4", { className: "font-black text-gray-900 mb-1" }, "Security"),
        React.createElement("p", { className: "text-gray-500 text-sm font-medium" }, "Update your account password"),
      ]),
      React.createElement("div", { key: "pass-field" }, [
        React.createElement("label", { className: "block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1" }, "New Password"),
        React.createElement("div", { className: "flex gap-3" }, [
          React.createElement("input", {
            key: "input",
            type: "password",
            value: newPassword,
            onChange: (e: any) => setNewPassword(e.target.value),
            className: "flex-1 bg-white/50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all font-medium",
            placeholder: "Minimum 6 characters"
          }),
          React.createElement("button", {
            key: "btn",
            onClick: handleUpdatePassword,
            disabled: updatingPassword || !newPassword,
            className: "bg-gray-900 hover:bg-black disabled:bg-gray-300 text-white font-bold px-6 rounded-2xl transition-all shadow-lg active:scale-[0.98] text-sm",
          }, updatingPassword ? "Updating..." : "Update")
        ])
      ])
    ]),

    React.createElement("div", { key: "billing-card", className: "bg-indigo-50 border border-indigo-100 rounded-[32px] p-8 flex items-center justify-between" }, [
      React.createElement("div", { key: "text" }, [
        React.createElement("h4", { className: "font-black text-indigo-900 mb-1" }, "SaaS Subscription"),
        React.createElement("p", { className: "text-indigo-700/70 text-sm font-medium" }, "You are currently on the Professional Plan"),
      ]),
      React.createElement("button", {
        key: "billing-btn",
        className: "bg-white text-indigo-600 font-bold px-6 py-3 rounded-xl shadow-sm hover:shadow-md transition-all text-sm",
        onClick: () => alert("Stripe Billing Integration coming soon!")
      }, "Manage Billing")
    ])
  ]);
}

// Main Findsy App Component
function FindsyApp() {
  const [activeAgent, setActiveAgent] = useState<AgentId>(null);
  const [routerMode, setRouterMode] = useState(false);
  const [currentView, setCurrentView] = useState<"chat" | "dashboard" | "upload" | "decisions" | "targets" | "notifications" | "settings">("dashboard");
  const [decisions, setDecisions] = useState<any[]>([]);
  const [targets, setTargets] = useState<Target[]>([]);
  const [targetNotifications, setTargetNotifications] = useState<TargetNotification[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [externalAlert, setExternalAlert] = useState<{ id?: string; message: string; agentType?: AgentId } | null>(null);
  const [prefillInput, setPrefillInput] = useState<string | null>(null);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [pendingDecisionContext, setPendingDecisionContext] = useState("");
  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [venueName, setVenueName] = useState<string>("");
  const [venueId, setVenueId] = useState<string>("");
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setAuthLoading(false);
      return;
    }

    // Check session on mount
    supabase.auth.getSession().then(({ data: { session } }: any) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        setVenueName(u.user_metadata?.venue_name || "");
        fetchVenueName(u.id);
      }
      setAuthLoading(false);
    });

    // Listen for changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        setVenueName(u.user_metadata?.venue_name || "");
        fetchVenueName(u.id);
      } else {
        setVenueName("");
        setVenueId("");
      }
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchVenueName = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('venue_id, venues(name)')
        .eq('id', userId)
        .single();

      if (data) {
        setVenueId(data.venue_id);
        if ((data as any).venues) {
          setVenueName((data as any).venues.name);
        }
      }
    } catch (e: any) {
      console.error("Error fetching venue details:", e);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setCurrentView("dashboard");
  };


  const checkAllTargets = async () => {
    const currentTargets = await supaFetchTargets();
    const currentNotifications = await supaFetchNotifications();

    let targetsChanged = false;
    let notifsChanged = false;

    for (const t of currentTargets) {
      const res = await evaluateTarget(t, files);
      t.lastCheckedAt = new Date().toISOString();

      if (res.value !== undefined && res.value !== t.lastValue) {
        t.lastValue = res.value;
        targetsChanged = true;
      }

      if (res.triggered) {
        if (t.status !== "alerting") {
          t.status = "alerting";
          targetsChanged = true;

          const hasUnread = currentNotifications.some(n => n.targetId === t.id && !n.read);
          if (!hasUnread) {
            const notif: TargetNotification = {
              id: `tn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              userId: t.userId,
              targetId: t.id,
              targetName: t.name,
              metric: t.metric,
              message: `Target '${t.name}' triggered: ${t.metric} is ${res.value}`,
              agentType: t.agentType,
              createdAt: new Date().toISOString(),
              read: false,
              value: res.value || 0,
              threshold: t.threshold,
              direction: t.direction,
            };
            await supaSaveNotification(notif);
            notifsChanged = true;
          }
        }
      } else if (t.status === "alerting") {
        t.status = "ok";
        targetsChanged = true;
      }

      if (targetsChanged) {
        await supaSaveTarget(t);
      }
    }

    if (targetsChanged || notifsChanged) {
      const [newT, newN] = await Promise.all([
        supaFetchTargets(),
        supaFetchNotifications()
      ]);
      setTargets(newT);
      setTargetNotifications(newN);
    }
  };

  useEffect(() => {
    // Initial check
    checkAllTargets();

    // Periodic check every 60s
    const interval = setInterval(checkAllTargets, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSaveDecision = (context: string) => {
    setPendingDecisionContext(context);
    setIsDecisionModalOpen(true);
  };

  const confirmSaveDecision = async (decision: string, expectedImpact: string) => {
    try {
      const result = await saveDecisionWorkflow({
        userId: user?.id || "user123",
        decision,
        context: pendingDecisionContext,
        expectedImpact,
        agentType: activeAgent,
      });

      setDecisions((prev) => [result, ...prev]);

      // Save to Supabase
      await supaSaveDecision(result);
      // alert("Decision saved successfully!");
    } catch (error) {
      console.error("Error saving decision:", error);
      alert("Failed to save decision");
    }
  };

  const handleFileUploaded = (fileRecord: any) => {
    console.log("File uploaded:", fileRecord);

    // Update local state immediately for instant feedback
    setFiles(prev => [fileRecord, ...prev]);

    (async () => {
      // Re-evaluate targets
      const currentTargets = getStoredTargets();
      if (currentTargets.length > 0) {
        const files = getStoredFiles();
        for (const t of currentTargets) {
          const res = await evaluateTarget(t, files);
          t.lastCheckedAt = new Date().toISOString();
          if (res.value !== undefined) t.lastValue = res.value;
          if (res.triggered) {
            if (t.status !== "alerting") {
              t.status = "alerting";
              const notif: TargetNotification = {
                id: `tn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                userId: t.userId,
                targetId: t.id,
                targetName: t.name,
                metric: t.metric,
                message: `Target '${t.name}' triggered: ${t.metric} is ${res.value}`,
                agentType: t.agentType,
                createdAt: new Date().toISOString(),
                read: false,
                value: res.value,
                threshold: t.threshold,
                direction: t.direction,
              };
              await supaSaveNotification(notif);
            }
          } else {
            t.status = "ok";
          }
          await supaSaveTarget(t);
        }
      }

      // Always refresh global state
      const [newTargets, newNotifs, newFiles] = await Promise.all([
        supaFetchTargets(),
        supaFetchNotifications(),
        supaFetchFilesMetadata(user.id)
      ]);
      setTargets(newTargets);
      setTargetNotifications(newNotifs);
      setFiles(newFiles);
    })();
  };

  const handleFileDeleted = (fileId: number) => {
    console.log("File deleted:", fileId);
    // Update local state immediately
    setFiles(prev => prev.filter(f => f.id !== fileId));

    // Refresh fully to ensure consistency
    (async () => {
      try {
        const newFiles = await supaFetchFilesMetadata(user.id);
        setFiles(newFiles);
      } catch (e) {
        console.error("Error refreshing files after delete", e);
      }
    })();
  };

  useEffect(() => {
    const initData = async () => {
      const [t, n, d, f] = await Promise.all([
        supaFetchTargets(),
        supaFetchNotifications(),
        supaFetchDecisions(),
        supaFetchFilesMetadata(user.id)
      ]);
      setTargets(t);
      setTargetNotifications(n);
      setDecisions(d);
      setFiles(f);
      // Sync local storage for helper functions that still use it
      storeFiles(f);
    };
    if (user) initData();
  }, [user]);

  const createNewTarget = () => {
    setIsTargetModalOpen(true);
  };

  const confirmCreateTarget = async (t: {
    name: string;
    metric: string;
    threshold: number;
    direction: "above" | "below";
    agentType: AgentId;
  }) => {
    // If files exist, store tags
    const files = getStoredFiles();
    const fileScopeTags = files.length > 0 ? files.map((f: any) => f.tags || []).flat().slice(0, 3) : [];

    const target: Target = {
      id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      userId: user?.id || "user123",
      name: t.name,
      metric: t.metric,
      threshold: t.threshold,
      direction: t.direction,
      tolerance: undefined,
      agentType: t.agentType,
      fileScopeTags,
      createdAt: new Date().toISOString(),
      status: "ok",
    };

    const current = [...targets];
    current.unshift(target);
    setTargets(current);
    setIsTargetModalOpen(false);

    // Save to Supabase
    await supaSaveTarget(target);
  };

  const runCheckForTarget = async (t: Target) => {
    // Set loading state
    t.isChecking = true;
    setTargets(prev => prev.map(tt => tt.id === t.id ? { ...t } : tt));

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));

    const files = getStoredFiles();
    const res = await evaluateTarget(t, files);
    t.lastCheckedAt = new Date().toISOString();
    t.isChecking = false;
    if (res.value !== undefined) t.lastValue = res.value;

    // Save state to Supabase
    await supaSaveTarget(t);

    if (res.triggered) {
      t.status = "alerting";
      const notifs = await supaFetchNotifications();
      // Only create a notification if there's no unread for this target
      const existingUnread = notifs.find(n => n.targetId === t.id && !n.read);
      if (!existingUnread) {
        const notif: TargetNotification = {
          id: `tn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          userId: t.userId,
          targetId: t.id,
          targetName: t.name,
          metric: t.metric,
          message: `Target '${t.name}' triggered: ${t.metric} is ${res.value}`,
          agentType: t.agentType,
          createdAt: new Date().toISOString(),
          read: false,
          value: res.value,
          threshold: t.threshold,
          direction: t.direction,
        };

        await supaSaveNotification(notif);
        setTargetNotifications([notif, ...notifs]);
      }
    } else {
      t.status = "ok";
    }
    const all = targets.map(tt => tt.id === t.id ? t : tt);
    setTargets(all);
  };

  const handleKpiExplain = (kpi: Kpi) => {
    // Determine which agent to route to based on KPI
    let targetAgent: AgentId = activeAgent;
    const kpiTitle = kpi.title.toLowerCase();

    if (kpiTitle.includes("revenue") || kpiTitle.includes("margin")) {
      targetAgent = "accountant";
    } else if (kpiTitle.includes("inventory") || kpiTitle.includes("ingredient")) {
      targetAgent = "chef";
    } else if (kpiTitle.includes("campaign") || kpiTitle.includes("marketing")) {
      targetAgent = "marketing";
    } else if (kpiTitle.includes("orders") || kpiTitle.includes("operations") || kpiTitle.includes("covers") || kpiTitle.includes("aov") || kpiTitle.includes("order value")) {
      targetAgent = "manager";
    }

    // Construct the message - simpler format as per requirements
    const message = `Explain: ${kpi.title}`;

    // Switch to chat view and prefill its input
    setCurrentView("chat");
    setActiveAgent(targetAgent);
    setPrefillInput(message);
  };

  const handleKpiManualFill = (kpi: Kpi) => {
    setCurrentView("chat");
    setActiveAgent(normalizeAgentId(kpi.metricKey === 'total_sales' || kpi.metricKey === 'gross_margin' ? 'accountant' : 'chef'));
    setPrefillInput(`I want to manually update the data for '${kpi.title}'. Please ask me what the value is so I can provide it.`);
  };

  if (!supabase) {
    return React.createElement("div", { className: "min-h-screen flex items-center justify-center p-4 bg-gray-50 text-center" }, [
      React.createElement("div", { className: "bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-red-100" }, [
        React.createElement("div", { className: "text-4xl mb-4" }, "🔌"),
        React.createElement("h2", { className: "text-xl font-bold text-gray-900 mb-2" }, "Configuration Required"),
        React.createElement("p", { className: "text-gray-500 mb-6 text-sm" }, "Findsy is ready, but your Supabase environment variables are missing. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your Netlify dashboard."),
      ])
    ]);
  }

  if (authLoading) {
    return React.createElement("div", {
      className: "min-h-screen flex items-center justify-center bg-white",
    }, React.createElement("div", { className: "text-center" }, [
      React.createElement("div", { key: "spinner", className: "w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" }),
      React.createElement("p", { key: "text", className: "text-gray-500 font-medium animate-pulse" }, "Loading your command center...")
    ]));
  }

  if (!user) {
    return React.createElement(AuthView, { onAuthSuccess: (u) => setUser(u) });
  }

  return React.createElement("div", {
    className: "min-h-screen",
    style: {
      background: "radial-gradient(1400px 700px at 30% 10%, rgba(99,102,241,.12) 0%, rgba(255,255,255,0) 60%), radial-gradient(1000px 600px at 75% 40%, rgba(139,92,246,.08) 0%, rgba(255,255,255,0) 55%), linear-gradient(180deg, rgba(249,250,251,1) 0%, rgba(243,244,246,1) 100%)",
    }
  }, [
    React.createElement(
      "header",
      { key: "header", className: "bg-white shadow-sm border-b" },
      React.createElement(
        "div",
        { className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" },
        React.createElement("div", { className: "flex justify-between items-center py-4" }, [
          React.createElement(
            "h1",
            { key: "title", className: "text-2xl font-bold text-gray-900 flex items-center" },
            React.createElement("img", {
              key: "logo",
              src: "/logo.png",
              alt: "Findsy",
              className: "h-8 w-auto",
            })
          ),

          React.createElement("div", { key: "user-nav", className: "flex items-center gap-4" }, [
            React.createElement("div", { key: "user-info", className: "hidden md:flex flex-col items-end" }, [
              React.createElement("span", { key: "venue", className: "text-sm font-black text-gray-900 tracking-tight" }, venueName || "Your Venue"),
              React.createElement("button", { key: "logout", onClick: handleLogout, className: "text-[10px] font-bold text-indigo-600 hover:text-indigo-700 uppercase tracking-widest transition-colors" }, "Sign Out")
            ]),
            React.createElement("button", {
              key: "logout-mobile",
              onClick: handleLogout,
              className: "md:hidden p-2 text-gray-400 hover:text-red-500 transition-colors"
            }, "🚪")
          ]),


          React.createElement(
            "div",
            { key: "controls", className: "flex items-center" },
            React.createElement(
              "button",
              {
                key: "router-toggle-btn",
                onClick: () => setRouterMode(!routerMode),
                style: {
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 16px",
                  borderRadius: 14,
                  border: routerMode ? "1px solid rgba(16,185,129,0.4)" : "1px solid rgba(255,255,255,0.5)",
                  background: routerMode
                    ? "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)"
                    : "rgba(255,255,255,0.4)",
                  boxShadow: routerMode
                    ? "0 4px 12px rgba(16,185,129,0.15), inset 0 0 0 1px rgba(255,255,255,0.2)"
                    : "0 2px 4px rgba(0,0,0,0.02)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  color: routerMode ? "rgb(5,150,105)" : "rgba(0,0,0,0.6)",
                  fontSize: 14,
                  fontWeight: 700,
                },
                onMouseEnter: (e: any) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.background = routerMode
                    ? "linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.15) 100%)"
                    : "rgba(255,255,255,0.6)";
                },
                onMouseLeave: (e: any) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.background = routerMode
                    ? "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)"
                    : "rgba(255,255,255,0.4)";
                }
              },
              [
                React.createElement("span", { key: "icon", style: { fontSize: 18, filter: routerMode ? "drop-shadow(0 0 5px rgba(16,185,129,0.5))" : "grayscale(1)" } }, "🤖"),
                React.createElement("span", { key: "text" }, "Router Mode"),
                React.createElement("div", {
                  key: "status",
                  style: {
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: routerMode ? "#10b981" : "#94a3b8",
                    boxShadow: routerMode ? "0 0 8px #10b981" : "none",
                    marginLeft: 2,
                    animation: routerMode ? "pulse 2s infinite" : "none",
                  }
                })
              ]
            )
          ),
        ])
      )
    ),

    React.createElement(
      "div",
      { key: "main", className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" },
      React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-4 gap-8" }, [
        React.createElement("div", { key: "sidebar", className: "lg:col-span-1 space-y-6" }, [
          // Dashboard button
          React.createElement(
            "button",
            {
              key: "dashboard-btn",
              onClick: () => setCurrentView("dashboard"),
              className: `w-full text-left px-4 py-3 rounded-lg font-semibold transition-all ${currentView === "dashboard"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`,
            },
            [
              React.createElement("div", { key: "icon", className: "inline-block mr-2" }, "📊"),
              "Dashboard",
            ]
          ),
          React.createElement(AgentSelector, {
            key: "agent-selector",
            activeAgent,
            onAgentChange: (agent: AgentId) => {
              setActiveAgent(agent);
              setCurrentView("chat");
            },
            routerMode,
          }),
          React.createElement(
            "button",
            {
              key: "upload-btn",
              onClick: () => setCurrentView("upload"),
              className: `w-full text-left px-4 py-3 rounded-lg font-semibold transition-all ${currentView === "upload"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`,
            },
            [
              React.createElement("div", { key: "icon", className: "inline-block mr-2" }, "📂"),
              "Upload Documents",
            ]
          ),
          React.createElement(
            "button",
            {
              key: "targets-btn",
              onClick: () => setCurrentView("targets"),
              className: `w-full text-left px-4 py-3 rounded-lg font-semibold transition-all ${currentView === "targets"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`,
            },
            [
              React.createElement("div", { key: "icon", className: "inline-block mr-2" }, "🎯"),
              "Targets",
            ]
          ),
          React.createElement(
            "button",
            {
              key: "decisions-btn",
              onClick: () => setCurrentView("decisions"),
              className: `w-full text-left px-4 py-3 rounded-lg font-semibold transition-all ${currentView === "decisions"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`,
            },
            [
              React.createElement("div", { key: "icon", className: "inline-block mr-2" }, "📋"),
              "Recent Decisions",
            ]
          ),
          React.createElement(
            "button",
            {
              key: "notifications-btn",
              onClick: () => setCurrentView("notifications"),
              className: `w-full text-left px-4 py-3 rounded-lg font-semibold transition-all ${currentView === "notifications"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`,
            },
            [
              React.createElement(
                "div",
                { key: "label", className: "flex items-center justify-between w-full" },
                [
                  React.createElement("span", { key: "text" }, [
                    React.createElement("span", { key: "icon", className: "mr-2" }, "🔔"),
                    "Notifications"
                  ]),
                  targetNotifications.filter(n => !n.read).length > 0 && React.createElement(
                    "span",
                    { key: "badge", className: "bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" },
                    targetNotifications.filter(n => !n.read).length
                  )
                ]
              )
            ]
          ),
          React.createElement(
            "button",
            {
              key: "settings-btn",
              onClick: () => setCurrentView("settings"),
              className: `w-full text-left px-4 py-3 rounded-lg font-semibold transition-all ${currentView === "settings"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`,
            },
            [
              React.createElement("div", { key: "icon", className: "inline-block mr-2" }, "⚙️"),
              "Venue Settings",
            ]
          ),
        ]
        ),

        React.createElement(
          "div",
          { key: "main-content", className: "lg:col-span-3" },
          currentView === "dashboard"
            ? React.createElement(DashboardView, {
              onKpiExplain: handleKpiExplain,
              onFillManual: handleKpiManualFill,
              uploadedFiles: files
            })
            : currentView === "upload"
              ? React.createElement(
                "div",
                { key: "upload-view", className: "h-full" },
                React.createElement(FileUpload, {
                  userId: user.id,
                  venueId: venueId,
                  onFileUploaded: handleFileUploaded,
                  onFileDeleted: handleFileDeleted,
                })
              )
              : currentView === "decisions"
                ? React.createElement(DecisionsView, { decisions })
                : currentView === "targets"
                  ? React.createElement(TargetsView, { targets, runCheckForTarget, createNewTarget })
                  : currentView === "notifications"
                    ? React.createElement(NotificationsView, {
                      notifications: targetNotifications,
                      onMarkAllRead: async () => {
                        const all = targetNotifications.map((n) => ({ ...n, read: true }));
                        setTargetNotifications(all);

                        // Update in Supabase
                        const { error } = await supabase
                          .from('notifications')
                          .update({ read: true })
                          .eq('read', false);
                        if (error) console.error("Supabase: Error marking all read", error);
                      },
                      onNotificationClick: async (n) => {
                        const all = [...targetNotifications];
                        const idx = all.findIndex((x) => x.id === n.id);
                        if (idx >= 0 && !all[idx].read) {
                          all[idx].read = true;
                          setTargetNotifications(all);
                          await supaMarkNotificationRead(n.id);
                        }
                        const msg = `ALERT: Our target '${n.targetName}' was missed.\n${n.metric} is ${n.value}, which is ${n.direction === 'above' ? 'above' : 'below'} the threshold of ${n.threshold}.\nWhat should we do?`;
                        setCurrentView("chat");
                        setActiveAgent(n.agentType);
                        setRouterMode(false);
                        setExternalAlert({ id: n.id, message: msg, agentType: n.agentType });
                      }
                    })
                    : currentView === "settings"
                      ? React.createElement(SettingsView, {
                        user,
                        venueName,
                        onUpdate: () => fetchVenueName(user.id)
                      })
                      : React.createElement(ChatInterface, {
                        activeAgent,
                        routerMode,
                        onSaveDecision: handleSaveDecision,
                        externalAlert: externalAlert,
                        onClearExternalAlert: () => setExternalAlert(null),
                        prefillInput: prefillInput,
                        onClearPrefill: () => setPrefillInput(null),
                        onAction: async (meta) => {
                          if (meta.UPDATE_KPI) {
                            const { key, value } = meta.UPDATE_KPI;
                            await supaUpdateManualMetric(user.id, venueId, key, parseFloat(value));
                            const f = await supaFetchFilesMetadata(user.id);
                            setFiles(f);
                          }
                        }
                      })
        ),
      ]),
    ),
    React.createElement(DecisionModal, {
      key: "decision-modal",
      isOpen: isDecisionModalOpen,
      onClose: () => setIsDecisionModalOpen(false),
      onSave: confirmSaveDecision,
      initialContext: pendingDecisionContext,
    }),
    React.createElement(TargetModal, {
      key: "target-modal",
      isOpen: isTargetModalOpen,
      onClose: () => setIsTargetModalOpen(false),
      onSave: confirmCreateTarget,
    }),
  ]);
}



// Route to multiple agents (2-3)
async function routeToMultipleAgents(
  input: string,
  availableAgents: AgentId[] = Object.keys(AGENT_PERSONAS) as AgentId[]
): Promise<AgentId[]> {
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY;

  const system = `You are a multi-agent routing analyzer for Findsy. Analyze the user's question and determine which 2-3 agents should collaborate to answer it.

Available agents: ${availableAgents.join(", ")}

Agent capabilities:
- chef: recipes, ingredients, food costs, menu pricing, kitchen operations
- manager: staffing, operations, compliance, workflows, service
- accountant: finances, margins, taxes, cash flow, break-even analysis
- marketing: promotions, social media, customer acquisition, branding

Respond with a comma-separated list of 2-3 agent names that best address this question. Example: "chef, accountant" or "marketing, manager". Do NOT return more than 3 agents.`;

  const assistant = await openaiChat(apiKey, [
    { role: "system", content: system },
    { role: "user", content: input },
  ]);

  const agentNames = String(assistant).split(",").map((s) => s.trim().toLowerCase());
  // Deduplicate and validate
  const uniqueAgents = Array.from(new Set(agentNames.map((name) => {
    try {
      return normalizeAgentId(name);
    } catch {
      return null;
    }
  }))).filter(Boolean) as AgentId[];

  // Return 1-3 agents; default to single if routing fails
  return uniqueAgents.length > 0 ? uniqueAgents.slice(0, 3) : ["chef"];
}

async function retrieveRelevantFiles(query: string, agentScope: AgentId) {
  console.log(`Retrieving files for agent: ${agentScope}, query: ${query}`);

  const allFiles = getStoredFiles();
  console.log(`Total files in storage: ${allFiles.length}`, allFiles);

  if (allFiles.length === 0 || !agentScope) return [];

  const agent = AGENT_PERSONAS[agentScope as ValidAgentId];
  const relevantFiles = allFiles.filter((file: any) =>
    file.tags?.some((tag: string) => (agent.allowedTags as readonly string[]).includes(tag))
  );

  console.log(`Relevant files for ${agentScope}:`, relevantFiles);

  if (relevantFiles.length === 0) return [];

  const queryLower = query.toLowerCase();
  const queryTerms = queryLower.split(" ").filter((t) => t.length > 2);

  const scoredFiles = relevantFiles.map((file: any) => {
    const contentLower = String(file.content || "").toLowerCase();
    let score = 0;

    queryTerms.forEach((term) => {
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = (contentLower.match(new RegExp(escapedTerm, "g")) || []).length;
      score += matches;
    });

    if (String(file.fileName || "").toLowerCase().includes(queryLower)) score += 10;

    return {
      ...file,
      relevanceScore: score,
      text: file.content,
      metadata: { fileName: file.fileName, tags: file.tags, uploadedAt: file.uploadedAt },
    };
  });

  const topFiles = scoredFiles.sort((a: any, b: any) => b.relevanceScore - a.relevanceScore).slice(0, 5);
  console.log(`Top relevant files for ${agentScope}:`, topFiles);

  return topFiles;
}

async function findsyWorkflow({
  input,
  agentType,
  conversationHistory = [],
  routerMode = false,
  env,
  tone,
  responseLength,
}: {
  input: string;
  agentType: AgentId;
  conversationHistory?: any[];
  routerMode?: boolean;
  env: { LANGBASE_API_KEY?: string; OPENAI_API_KEY?: string };
  // Optional tone only applies to the marketing agent
  tone?: "calm" | "warm" | "firm";
  responseLength?: "short" | "normal";
}) {
  // Router mode: select multiple agents (2-3) based on question domain
  // Manual mode: use the single selected agent
  const selectedAgents: AgentId[] = routerMode
    ? await routeToMultipleAgents(input)
    : [normalizeAgentId(agentType)];

  // If multiple agents were routed, run workflows in parallel and combine results
  if (selectedAgents.length > 1) {
    const parallelResults = await Promise.all(
      selectedAgents.map((agentId) =>
        runSingleAgentWorkflow(input, agentId, conversationHistory, env, tone, responseLength)
      )
    );

    // Filter out non-substantive responses (mostly questions or deferring to other agents)
    // Keep responses that are direct advice and actionable
    const substantiveResults = parallelResults.filter((r) => {
      const response = String(r.response || "").toLowerCase();
      const hasQuestion = response.includes("can you") || response.includes("could you") ||
        response.includes("please provide") || response.includes("please share") ||
        response.includes("please upload") || response.includes("i need");
      // Only include if response is substantive (not mostly asking for info)
      const isSubstantive = response.length > 100 && !hasQuestion;
      return isSubstantive;
    });

    // If all responses were filtered out, include all but mark them
    const resultsToUse = substantiveResults.length > 0 ? substantiveResults : parallelResults;

    // Format responses with clear separation and agent headers
    const formattedResponses = resultsToUse.map((r, idx) =>
      `${r.agentName}:\n${r.response}${idx < resultsToUse.length - 1 ? "\n" : ""}`
    ).join("\n\n");

    // Return combined results with multiple agents
    return {
      response: formattedResponses,
      agent: "multiple",
      agentName: selectedAgents.map((a) => AGENT_PERSONAS[a as ValidAgentId].name).join(" + "),
      agentIds: selectedAgents,
      filesUsed: parallelResults.reduce((sum, r) => sum + r.filesUsed, 0),
      citations: parallelResults.flatMap((r) => r.citations),
      actionMeta: parallelResults.find((r) => r.actionMeta)?.actionMeta || null,
    };
  }

  // Single agent workflow
  return runSingleAgentWorkflow(input, selectedAgents[0], conversationHistory, env, tone, responseLength);
}

// Helper: synthesize multi-agent responses into unified, coordinated format
// Helper: run workflow for a single agent
async function runSingleAgentWorkflow(
  input: string,
  selectedAgent: AgentId,
  conversationHistory: any[],
  env: { LANGBASE_API_KEY?: string; OPENAI_API_KEY?: string },
  tone?: "calm" | "warm" | "firm",
  responseLength?: "short" | "normal"
) {

  const relevantFiles = await retrieveRelevantFiles(input, selectedAgent);

  const agent = AGENT_PERSONAS[selectedAgent as ValidAgentId];

  const MAX_FILE_CHARS = 4000;
  let fileContext = "";
  if (relevantFiles.length > 0) {
    fileContext =
      `\n\nUPLOADED FILES AVAILABLE (${relevantFiles.length}). Use them when relevant.\n` +
      relevantFiles
        .map((file: any, index: number) => {
          const content = String(file.content || "");
          const snippet =
            content.length > MAX_FILE_CHARS
              ? content.slice(0, MAX_FILE_CHARS) + "\n[TRUNCATED]"
              : content;
          return `FILE ${index + 1}: ${file.fileName} (Tags: ${file.tags.join(
            ", "
          )})\nContent:\n${snippet}\n---`;
        })
        .join("\n\n");
  } else {
    fileContext =
      "\n\nNO RELEVANT FILES FOUND for this agent. The user may need to upload documents such as:\n- Menu items and recipes\n- Ingredient costs and supplier invoices\n- Sales reports\n- Staff schedules\n- Compliance documents";
  }

  const conversationContext =
    conversationHistory.length > 0
      ? `\n\nPREVIOUS CONVERSATION:\n${conversationHistory
        .map((msg: any) => `${msg.role}: ${msg.content}`)
        .join("\n")}`
      : "";

  const openaiKey = env.OPENAI_API_KEY || import.meta.env.VITE_OPENAI_API_KEY;
  // If this is the marketing agent and a tone is supplied, include explicit tone guidance
  const toneInstruction =
    selectedAgent === "marketing" && tone
      ? `\n\nTONE INSTRUCTION: When writing public review replies, match the selected tone exactly: calm = apologetic, steady, professional; warm = empathetic, friendly, reassuring; firm = polite, confident, sets expectations. Use the user's selected tone.`
      : "";

  // Length instruction (only for 'short')
  const lengthInstruction =
    responseLength === "short"
      ? "\n\nCRITICAL LENGTH INSTRUCTION: Your response must be extremely brief (max 2 sentences). Be direct and blunt. Skip any polite filler, reasoning, or multiple sections. Word count target: < 30 words."
      : "";

  const responseFormat = responseLength === "short"
    ? "RESPONSE FORMAT:\n- A single direct recommendation\n- (Optional) One short metric or reason\n- NO headers, NO next steps, NO intro/outro."
    : "RESPONSE FORMAT:\n1. Direct answer to the user's question\n2. Show calculations/reasoning when relevant\n3. Cite specific files if used: [FileName: specific data]\n4. End with \"NEXT STEPS:\" and 2-3 actionable items\n5. Keep your response focused and practical (not exploratory)";

  // In multi-agent mode, instruct agents to provide answers based on available context
  const multiAgentInstruction = conversationHistory.length === 0 && selectedAgent !== "marketing"
    ? "\n\nIMPORTANT: You are responding as part of a multi-agent team. Provide your best actionable advice based on the available context. Do NOT ask the user for more information or defer to another agent. Give a direct, confident answer in your domain."
    : "";

  const systemPrompt = `${agent.instructions}${toneInstruction}${lengthInstruction}${fileContext}${conversationContext}${multiAgentInstruction}\n\nIMPORTANT INSTRUCTIONS:\n- If you have uploaded files available, USE THEM to provide specific, data-driven advice\n- Always cite the specific file name and data when referencing uploaded information\n- If no relevant files are available, still provide practical, actionable advice based on industry best practices\n- Provide direct, confident advice based on the available information\n- If the user provides a specific numeric value for a KPI (like revenue, covers, margin, cost), you MUST include an ACTION block at the very end of your response.\n  Format:\n  ACTION: UPDATE_KPI\n  key: metric_key\n  value: 5000\n  Keys: total_sales, covers, aov, gross_margin, food_cost_pct, waste_rate.\n- Do NOT use markdown formatting like ** or __ or # — use plain text only\n- Do NOT ask the user for more information; provide your best answer with what you have\n\n${responseFormat}\n\nExample assistant reply (standard):\nShort: "Raise the price to £10.50 and test for 2 weeks."\nSupport: "Based on your £2.50 cost, this gives you 38% margin, which is healthy for this dish."`;

  console.log(`[runSingleAgentWorkflow] Agent: ${selectedAgent}, Length: ${responseLength}, Tone: ${tone}`);


  // Call the LLM
  const assistantOutput = await openaiChat(openaiKey, [
    { role: "system", content: systemPrompt },
    { role: "user", content: input },
  ], "gpt-4o-mini", 0.7);

  // Detect an ACTION block appended by the assistant (used for UI actions)
  let actionMeta: any = null;
  let outputWithoutAction = String(assistantOutput || "");
  const actionStart = outputWithoutAction.indexOf("ACTION:");
  if (actionStart !== -1) {
    const actionBlock = outputWithoutAction.slice(actionStart);
    const actionLines = actionBlock.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (actionLines.length > 0) {
      const actionType = actionLines[0].replace("ACTION:", "").trim();
      const meta: any = {};
      actionLines.slice(1).forEach((l) => {
        const parts = l.split(":");
        if (parts.length >= 2) {
          meta[parts[0].trim()] = parts.slice(1).join(":").trim();
        }
      });
      actionMeta = { [actionType]: meta };
    }
    outputWithoutAction = outputWithoutAction.slice(0, actionStart).trim();
  }

  const cleanedOutput = sanitizeOutput(outputWithoutAction);

  return {
    response: cleanedOutput,
    agent: selectedAgent,
    agentName: agent.name,
    filesUsed: relevantFiles.length,
    citations: relevantFiles.map((file: any) => ({
      fileName: file.fileName,
      snippet: String(file.content || "").substring(0, 150) + "...",
      tags: file.tags,
    })),
    // Additional metadata for UI actions (e.g., review replies)
    actionMeta,
  };
}

// Decision saving workflow (kept simple)
async function saveDecisionWorkflow({
  userId,
  decision,
  context,
  expectedImpact,
  agentType,
  targetId,
}: {
  userId: string;
  decision: string;
  context: string;
  expectedImpact: string;
  agentType: AgentId;
  targetId?: string;
}) {
  const decisionRecord = {
    id: `decision_${Date.now()}`,
    userId,
    agentType,
    decision,
    context,
    expectedImpact,
    targetId: targetId || null,
    timestamp: new Date().toISOString(),
    status: "active",
  };

  console.log("Decision saved:", decisionRecord);
  return decisionRecord;
}

async function main(event: any, env: any) {
  try {
    console.log("Event received:", event);

    // UI render request
    if (event && (event.type === "ui" || event.method === "GET" || !event.json)) {
      return React.createElement(FindsyApp);
    }

    // API requests
    let payload: any;
    try {
      payload = await event.json();
    } catch (jsonError) {
      console.error("JSON parsing error:", jsonError);
      payload = {
        action: "chat",
        input:
          event.input ||
          event.query ||
          "Hello! I'm looking for help with my food vendor business.",
        agentType: "chef",
        userId: "user123",
        conversationHistory: [],
        routerMode: false,
      };
    }

    console.log("Payload:", payload);

    const action = payload.action || "chat";

    switch (action) {
      case "chat": {
        const result = await findsyWorkflow({
          input:
            payload.input ||
            "Hello! I'm looking for help with my food vendor business.",
          agentType: normalizeAgentId(payload.agentType),
          routerMode: !!payload.routerMode,
          env,
          tone: payload.tone,
        });
        return result;
      }

      case "saveDecision": {
        const decision = await saveDecisionWorkflow({
          userId: payload.userId || "user123",
          decision: payload.decision || "No decision specified",
          context: payload.context || "No context provided",
          expectedImpact: payload.expectedImpact || "No impact specified",
          agentType: normalizeAgentId(payload.agentType),
        });
        return decision;
      }

      default: {
        console.log(`Unknown action: ${action}, defaulting to chat`);
        const defaultResult = await findsyWorkflow({
          input:
            payload.input ||
            "Hello! I'm looking for help with my food vendor business.",
          agentType: normalizeAgentId(payload.agentType),
          routerMode: !!payload.routerMode,
          env,
          tone: payload.tone,
        });
        return defaultResult;
      }
    }
  } catch (error: any) {
    console.error("Main function error:", error);

    return {
      response:
        "Welcome to Findsy! I'm here to help you with your food vendor business. You can ask me about recipes, costs, operations, finances, or marketing. What would you like to know?",
      agent: "chef",
      agentName: "Chef / Inventory Agent",
      filesUsed: 0,
      citations: [],
      error: error?.message || "unknown error",
    };
  }
}

export default main;


